import React, { useEffect, useMemo, useState, useRef, createContext, useContext, Children, Fragment, createElement, Component } from 'react';
import { useUser } from '../contexts/UserContext';
import { DollarSignIcon, HomeIcon, ChevronRightIcon, LayoutDashboardIcon, KanbanSquareIcon, ListIcon, AlertCircleIcon, ClockIcon, CalendarIcon, TrendingUpIcon, UsersIcon, UserIcon, GlobeIcon, BuildingIcon, Building2Icon, NetworkIcon, GripVerticalIcon, PanelRightCloseIcon, PanelRightOpenIcon, Settings2Icon, ChevronDownIcon, SlidersHorizontalIcon, XIcon, CheckCircleIcon, XCircleIcon, SearchIcon, FilterIcon, MoreVerticalIcon, PlusIcon, ArrowUpDownIcon, ArrowUpIcon, ArrowDownIcon, DownloadIcon, FileTextIcon, StickyNoteIcon, ChevronLeftIcon, EyeIcon, MessageSquareIcon, SendIcon, LockIcon } from 'lucide-react';
// --- Types ---
type NavItem = 'overview' | 'pipeline' | 'quotes' | 'business-units' | 'dealers' | 'attention' | 'followups' | 'timeline';
type QuoteStatus = 'Draft' | 'Active' | 'Expired' | 'Won' | 'Lost' | 'Cancelled';
type QuoteProductType = 'pivot' | 'spareparts' | 'project';
interface Quote {
  id: string;
  customer: string;
  dealer: string;
  country: string;
  amount: number;
  probability: number;
  status: QuoteStatus;
  date: string;
  lastUpdated: string;
  needsAttention?: boolean;
  attentionReason?: string;
  businessUnit: string;
  salesManager: string;
  // Optional refined fields — fall back to derived values when missing
  name?: string;
  quotedBy?: string;
  expiresAt?: string;
  productType?: QuoteProductType;
  productCount?: number;
  notes?: QuoteNote[];
}
// --- Helpers for derived quote fields ---
const PRODUCT_TYPE_LABEL: Record<QuoteProductType, string> = {
  pivot: 'pivot',
  spareparts: 'spare parts',
  project: 'project'
};
function hashCode(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}
function getQuoteName(q: Quote): string {
  if (q.name) return q.name;
  const type = getProductType(q);
  const label = type === 'pivot' ? 'Pivot Package' : type === 'spareparts' ? 'Spare Parts Order' : 'Project';
  return `${q.customer} — ${label}`;
}
function getQuotedBy(q: Quote): string {
  return q.quotedBy ?? q.salesManager;
}
function getProductType(q: Quote): QuoteProductType {
  if (q.productType) return q.productType;
  const types: QuoteProductType[] = ['pivot', 'spareparts', 'project'];
  return types[hashCode(q.id) % types.length];
}
function getProductCount(q: Quote): number {
  if (typeof q.productCount === 'number') return q.productCount;
  const type = getProductType(q);
  const seed = hashCode(q.id);
  if (type === 'pivot') return 1 + seed % 8; // 1–8 pivots
  if (type === 'spareparts') return 5 + seed % 45; // 5–49 parts
  return 1; // 1 project
}
function addDaysISO(iso: string, days: number): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}
function getExpiresAt(q: Quote): string {
  return q.expiresAt ?? addDaysISO(q.date, 30);
}
function formatShortDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return iso;
  }
}
function formatLongDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return iso;
  }
}
// --- Notes & Timeline ---
type NoteKind = 'closure' | 'follow-up' | 'general';
interface QuoteNote {
  kind: NoteKind;
  date: string;
  author: string;
  text: string;
}
type ActivityKind = 'created' | 'updated' | 'note' | 'status-change' | 'discount' | 'items-changed';
interface ActivityEvent {
  date: string;
  kind: ActivityKind;
  title: string;
  detail?: string;
  author?: string;
  // For status-change: target status
  toStatus?: QuoteStatus;
  // For notes: bind the matching note
  note?: QuoteNote;
}
// Realistic loss/cancellation reasons for synthetic notes
const CLOSURE_REASONS_LOST: string[] = ['Lost to competitor on price — John Deere offered ~12% below our quote. Customer prioritised CAPEX over service. Revisit in Q3 with bundled service plan.', 'Customer postponed project to 2027 due to budget cuts. Land already prepared; revisit when financing reopens.', 'Spec changed mid-cycle: customer needed corner system, we couldn’t deliver in time. Lost to local integrator.', 'Financing fell through. Customer chose to lease equipment from a rental dealer instead. Keep warm — interested in 2027.', 'Competitor won on lead time (4 weeks vs our 9). Production capacity issue. Recommend escalating with ops.'];
const CLOSURE_REASONS_CANCELLED: string[] = ['Customer cancelled internally — pivot field was sold off as part of a larger land deal. No replacement project.', 'Cancelled by customer after well water tests showed insufficient flow. Recommended alternative drip system from partner.', 'Cancelled — customer never returned signed PO after multiple follow-ups. Lead went cold.', 'Project scope changed: customer pivoted to greenhouse cultivation. No longer a fit.'];
const CLOSURE_REASONS_WON: string[] = ['Won on bundled service plan + 24-month parts warranty. Beat competitor by adding installation training, no price cut needed.', 'Won despite higher price thanks to existing relationship with Sales Manager. Customer cited service reliability as decisive factor.', 'Closed quickly — customer renewed with us after a positive previous experience. No discount applied.'];
function pickFrom<T>(arr: T[], seed: number): T {
  return arr[Math.abs(seed) % arr.length];
}
function getNotes(q: Quote): QuoteNote[] {
  if (q.notes && q.notes.length > 0) return q.notes;
  const seed = hashCode(q.id);
  // Synthesize closure notes for closed states
  if (q.status === 'Lost') {
    return [{
      kind: 'closure',
      date: q.lastUpdated,
      author: getQuotedBy(q),
      text: pickFrom(CLOSURE_REASONS_LOST, seed)
    }];
  }
  if (q.status === 'Cancelled') {
    return [{
      kind: 'closure',
      date: q.lastUpdated,
      author: getQuotedBy(q),
      text: pickFrom(CLOSURE_REASONS_CANCELLED, seed)
    }];
  }
  if (q.status === 'Won' && seed % 3 === 0) {
    // ~1/3 of Wons get a note
    return [{
      kind: 'closure',
      date: q.lastUpdated,
      author: getQuotedBy(q),
      text: pickFrom(CLOSURE_REASONS_WON, seed)
    }];
  }
  // Occasional follow-up note on Active quotes (~1/7)
  if (q.status === 'Active' && seed % 7 === 0) {
    return [{
      kind: 'follow-up',
      date: q.lastUpdated,
      author: getQuotedBy(q),
      text: 'Customer requested updated spec sheet and a site visit before signing. Follow-up scheduled.'
    }];
  }
  return [];
}
function hasNote(q: Quote): boolean {
  return getNotes(q).length > 0;
}
function getClosureNote(q: Quote): QuoteNote | null {
  return getNotes(q).find(n => n.kind === 'closure') ?? null;
}
// Build a synthetic activity timeline for a quote (descending: most recent first)
function getTimeline(q: Quote): ActivityEvent[] {
  const events: ActivityEvent[] = [];
  // Status-change event if the quote is in a final/changed state
  if (['Won', 'Lost', 'Cancelled', 'Expired'].includes(q.status)) {
    events.push({
      date: q.lastUpdated,
      kind: 'status-change',
      title: `Marked as ${q.status}`,
      toStatus: q.status,
      author: getQuotedBy(q)
    });
  }
  // Notes (closure note inlined here)
  for (const n of getNotes(q)) {
    events.push({
      date: n.date,
      kind: 'note',
      title: n.kind === 'closure' ? 'Closure note added' : n.kind === 'follow-up' ? 'Follow-up note' : 'Note added',
      detail: n.text,
      author: n.author,
      note: n
    });
  }
  // Synth a discount / items event for higher-amount quotes
  const seed = hashCode(q.id);
  if (q.amount > 80000 && seed % 2 === 0) {
    events.push({
      date: addDaysISO(q.date, Math.max(2, seed % 10 + 2)),
      kind: 'discount',
      title: `Discount applied (-${5 + seed % 8}%)`,
      author: getQuotedBy(q)
    });
  }
  if (seed % 3 === 0) {
    events.push({
      date: addDaysISO(q.date, 1 + seed % 5),
      kind: 'items-changed',
      title: 'Items updated',
      author: getQuotedBy(q)
    });
  }
  // Update event if lastUpdated differs from date
  if (q.lastUpdated && q.lastUpdated !== q.date) {
    events.push({
      date: q.lastUpdated,
      kind: 'updated',
      title: 'Quote updated',
      author: getQuotedBy(q)
    });
  }
  // Created (always)
  events.push({
    date: q.date,
    kind: 'created',
    title: 'Quote created',
    author: getQuotedBy(q)
  });
  // Sort descending and deduplicate same date+kind+title by keeping the more informative one
  return events.sort((a, b) => {
    const da = new Date(a.date).getTime();
    const db = new Date(b.date).getTime();
    return db - da;
  });
}
// --- Dummy Data ---
export const dummyQuotes: Quote[] = [
// Aisco EUROPE — Olexandr Morozov
{
  id: 'QT-2026-001',
  customer: 'AgriCorp',
  dealer: 'Zeppelin',
  country: 'Ukraine',
  amount: 125000,
  probability: 80,
  status: 'Active',
  date: '2026-05-01',
  lastUpdated: '2026-05-18',
  needsAttention: true,
  attentionReason: 'Price validity expires in 2 days',
  businessUnit: 'Aisco EUROPE',
  salesManager: 'Olexandr Morozov'
}, {
  id: 'QT-2026-003',
  customer: 'Valley Farms',
  dealer: 'Zeppelin',
  country: 'Ukraine',
  amount: 210000,
  probability: 90,
  status: 'Active',
  date: '2026-04-20',
  lastUpdated: '2026-05-17',
  businessUnit: 'Aisco EUROPE',
  salesManager: 'Olexandr Morozov'
}, {
  id: 'QT-2026-006',
  customer: 'River Basin',
  dealer: 'Zeppelin',
  country: 'Ukraine',
  amount: 150000,
  probability: 20,
  status: 'Draft',
  date: '2026-05-18',
  lastUpdated: '2026-05-19',
  businessUnit: 'Aisco EUROPE',
  salesManager: 'Olexandr Morozov'
},
// Aisco EUROPE — Javier Adrados
{
  id: 'QT-2026-002',
  customer: 'GreenFields',
  dealer: 'AgroTech',
  country: 'Spain',
  amount: 85000,
  probability: 40,
  status: 'Active',
  date: '2026-05-10',
  lastUpdated: '2026-05-15',
  businessUnit: 'Aisco EUROPE',
  salesManager: 'Javier Adrados'
}, {
  id: 'QT-2026-005',
  customer: 'Highland',
  dealer: 'AgroTech',
  country: 'Spain',
  amount: 45000,
  probability: 0,
  status: 'Lost',
  date: '2026-03-15',
  lastUpdated: '2026-04-01',
  businessUnit: 'Aisco EUROPE',
  salesManager: 'Javier Adrados'
}, {
  id: 'QT-2026-007',
  customer: 'SunCrop',
  dealer: 'AgroTech',
  country: 'Spain',
  amount: 95000,
  probability: 60,
  status: 'Active',
  date: '2026-05-12',
  lastUpdated: '2026-05-12',
  businessUnit: 'Aisco EUROPE',
  salesManager: 'Javier Adrados'
},
// Aisco EUROPE — Régis SIMIER
{
  id: 'QT-2026-008',
  customer: 'Champagne Vins',
  dealer: 'IrrigaFrance',
  country: 'France',
  amount: 178000,
  probability: 70,
  status: 'Active',
  date: '2026-05-04',
  lastUpdated: '2026-05-18',
  businessUnit: 'Aisco EUROPE',
  salesManager: 'Régis SIMIER'
}, {
  id: 'QT-2026-009',
  customer: 'Loire Vallée',
  dealer: 'IrrigaFrance',
  country: 'France',
  amount: 62000,
  probability: 50,
  status: 'Active',
  date: '2026-05-11',
  lastUpdated: '2026-05-14',
  businessUnit: 'Aisco EUROPE',
  salesManager: 'Régis SIMIER'
},
// Aisco MENA — Mohamed S. Aloraini
{
  id: 'QT-2026-004',
  customer: 'Oasis Ag',
  dealer: 'Desert Bloom',
  country: 'Egypt',
  amount: 340000,
  probability: 100,
  status: 'Won',
  date: '2026-04-05',
  lastUpdated: '2026-05-10',
  businessUnit: 'Aisco MENA',
  salesManager: 'Mohamed S. Aloraini'
}, {
  id: 'QT-2026-010',
  customer: 'Nile Greens',
  dealer: 'Desert Bloom',
  country: 'Egypt',
  amount: 145000,
  probability: 75,
  status: 'Active',
  date: '2026-05-02',
  lastUpdated: '2026-05-17',
  needsAttention: true,
  attentionReason: 'No follow-up activity in 14 days',
  businessUnit: 'Aisco MENA',
  salesManager: 'Mohamed S. Aloraini'
},
// Aisco MENA — John Quirk
{
  id: 'QT-2026-011',
  customer: 'Gulf Agro',
  dealer: 'Saudi Irrigation',
  country: 'Saudi Arabia',
  amount: 420000,
  probability: 85,
  status: 'Active',
  date: '2026-04-15',
  lastUpdated: '2026-05-15',
  businessUnit: 'Aisco MENA',
  salesManager: 'John Quirk'
}, {
  id: 'QT-2026-012',
  customer: 'Red Sea Farms',
  dealer: 'Saudi Irrigation',
  country: 'Saudi Arabia',
  amount: 95000,
  probability: 40,
  status: 'Cancelled',
  date: '2026-05-08',
  lastUpdated: '2026-05-12',
  businessUnit: 'Aisco MENA',
  salesManager: 'John Quirk'
},
// Aisco AMERICA — Diego Solis
{
  id: 'QT-2026-013',
  customer: 'AgroMex',
  dealer: 'Riego Norte',
  country: 'Mexico',
  amount: 188000,
  probability: 65,
  status: 'Active',
  date: '2026-05-06',
  lastUpdated: '2026-05-18',
  businessUnit: 'Aisco AMERICA',
  salesManager: 'Diego Solis'
}, {
  id: 'QT-2026-014',
  customer: 'Sonora Crops',
  dealer: 'Riego Norte',
  country: 'Mexico',
  amount: 76000,
  probability: 100,
  status: 'Won',
  date: '2026-03-20',
  lastUpdated: '2026-04-22',
  businessUnit: 'Aisco AMERICA',
  salesManager: 'Diego Solis'
},
// Aisco AMERICA — Chris Spelic
{
  id: 'QT-2026-015',
  customer: 'Midwest Harvest',
  dealer: 'Iowa Pivot Co',
  country: 'United States',
  amount: 265000,
  probability: 90,
  status: 'Active',
  date: '2026-04-28',
  lastUpdated: '2026-05-16',
  businessUnit: 'Aisco AMERICA',
  salesManager: 'Chris Spelic'
}, {
  id: 'QT-2026-016',
  customer: 'Prairie Ag',
  dealer: 'Iowa Pivot Co',
  country: 'United States',
  amount: 132000,
  probability: 55,
  status: 'Expired',
  date: '2026-05-09',
  lastUpdated: '2026-05-14',
  needsAttention: true,
  attentionReason: 'Quote pending response for 9 days',
  businessUnit: 'Aisco AMERICA',
  salesManager: 'Chris Spelic'
}, {
  id: 'QT-2026-017',
  customer: 'Heartland Farms',
  dealer: "ABE'S IRRIGATION",
  country: 'United States',
  amount: 89000,
  probability: 30,
  status: 'Active',
  date: '2026-05-14',
  lastUpdated: '2026-05-14',
  businessUnit: 'Aisco AMERICA',
  salesManager: 'Chris Spelic'
},
// Aisco AFRICA — Khaled Shalabi
{
  id: 'QT-2026-018',
  customer: 'Sahara Greens',
  dealer: 'Maghreb Agro',
  country: 'Morocco',
  amount: 156000,
  probability: 60,
  status: 'Active',
  date: '2026-05-03',
  lastUpdated: '2026-05-15',
  businessUnit: 'Aisco AFRICA',
  salesManager: 'Khaled Shalabi'
}, {
  id: 'QT-2026-019',
  customer: 'Atlas Farms',
  dealer: 'Maghreb Agro',
  country: 'Morocco',
  amount: 68000,
  probability: 0,
  status: 'Lost',
  date: '2026-02-28',
  lastUpdated: '2026-03-25',
  businessUnit: 'Aisco AFRICA',
  salesManager: 'Khaled Shalabi'
}];
export const QuotesContext = createContext<Quote[]>(dummyQuotes);
export function useQuotes() {
  return useContext(QuotesContext);
}
// --- Helper Components ---
function StatusBadge({
  status
}: {
  status: QuoteStatus;
}) {
  const colors: Record<QuoteStatus, string> = {
    Draft: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
    Active: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    Won: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    Lost: 'bg-red-500/20 text-red-300 border-red-500/30',
    Expired: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
    Cancelled: 'bg-gray-500/20 text-gray-300 border-gray-500/30'
  };
  return <span className={`px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${colors[status]}`} data-id="element-11991" data-mp="26kroewvkj0vm">
      {status}
    </span>;
}
function KPICard({
  title,
  value,
  subtitle,
  icon,
  trend
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
}) {
  return <div className="bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm" data-id="element-11992" data-mp="3rvrk9pxjucw9">
      <div className="flex items-start justify-between mb-2" data-id="element-11993" data-mp="2venqu5l2ahu">
        <div className="p-2 bg-white/5 rounded-lg text-blue-400" data-id="element-11994" data-mp="2wzfkc0ijfc0">{icon}</div>
        {trend && <div className={`flex items-center text-xs font-medium ${trend.isPositive ? 'text-emerald-400' : 'text-red-400'}`} data-id="element-11995" data-mp="7vsveb39vifc">
            {trend.isPositive ? <TrendingUpIcon size={14} className="mr-1" data-id="element-11996" data-mp="hs5p38ythwa3" /> : <TrendingUpIcon size={14} className="mr-1 rotate-180" data-id="element-11997" data-mp="hpt9v3kg200q" />}
            {trend.value}%
          </div>}
      </div>
      <h3 className="text-2xl font-bold text-white mb-1" data-id="element-11998" data-mp="3l8m8ma8kckau">{value}</h3>
      <p className="text-sm text-blue-300/70" data-id="element-11999" data-mp="3l8w0y4h2z3jp">{title}</p>
      {subtitle && <p className="text-xs text-blue-300/50 mt-1" data-id="element-12000" data-mp="3q6o1fmvsjxzi">{subtitle}</p>}
    </div>;
}
// --- Views ---
function OverviewView({
  role
}: {
  role: string;
}) {
  const dummyQuotes = useQuotes();
  const openPipeline = dummyQuotes.filter(q => !['Won', 'Lost', 'Expired', 'Cancelled'].includes(q.status)).reduce((acc, q) => acc + q.amount, 0);
  const weightedPipeline = dummyQuotes.filter(q => !['Won', 'Lost', 'Expired', 'Cancelled'].includes(q.status)).reduce((acc, q) => acc + q.amount * q.probability / 100, 0);
  const wonAmount = dummyQuotes.filter(q => q.status === 'Won').reduce((acc, q) => acc + q.amount, 0);
  let title = 'Global Overview';
  if (['manager-business-unit', 'region-admin'].includes(role)) title = 'BU Control Center';
  if (role === 'sales-manager') title = 'Territory Workbench';
  if (['dealer', 'dealer-admin'].includes(role)) title = 'My Quotes';
  return <div className="space-y-6" data-id="element-12001" data-mp="vxo28dmnlfd8">
      <div className="flex items-center justify-between" data-id="element-12002" data-mp="vwmhtijqzgiu">
        <h2 className="text-xl font-bold text-white" data-id="element-12003" data-mp="rvkrxsbq0gtj">{title}</h2>
        <button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-12004" data-mp="rw2okq2aox1z">
          <PlusIcon size={16} data-id="element-12005" data-mp="hx50fo8s75ji" />
          New Quote
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-12006" data-mp="czlc1viddcdu">
        <KPICard title="Open Pipeline" value={`$${openPipeline.toLocaleString()}`} icon={<DollarSignIcon size={20} data-id="element-12008" data-mp="25uchwipu87ax" />} trend={{
        value: 12.5,
        isPositive: true
      }} data-id="element-12007" data-mp="21s7tbye8y0v5" />
        <KPICard title="Weighted Pipeline" value={`$${weightedPipeline.toLocaleString()}`} icon={<TrendingUpIcon size={20} data-id="element-12010" data-mp="1rwxsvf3hy1jh" />} data-id="element-12009" data-mp="1rujtwv5p456n" />
        <KPICard title="Won (YTD)" value={`$${wonAmount.toLocaleString()}`} icon={<CheckCircleIcon size={20} data-id="element-12012" data-mp="1i50ddw5ggney" />} trend={{
        value: 5.2,
        isPositive: true
      }} data-id="element-12011" data-mp="1wsv3r5vzqfov" />
        <KPICard title="Win Rate" value="42%" icon={<TrendingUpIcon size={20} data-id="element-12014" data-mp="1mx1lr6zi5tzp" />} data-id="element-12013" data-mp="1mv8fhsssxbs8" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-12015" data-mp="194bzd1g2an22">
        <div className="lg:col-span-2 bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm" data-id="element-12016" data-mp="195wpmevcfju4">
          <h3 className="text-lg font-semibold text-white mb-4" data-id="element-12017" data-mp="1e12f1nnyfgmy">
            Recent Activity
          </h3>
          <div className="space-y-4" data-id="element-12018" data-mp="z88nh3vera1x">
            {dummyQuotes.slice(0, 4).map(quote => <div key={quote.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors border border-white/5" data-id="element-12019" data-mp="1447e6y47l45i">
                <div className="flex items-center gap-4" data-id="element-12020" data-mp="gincdva1a113">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400" data-id="element-12021" data-mp="uhd8p8p13woe">
                    <DollarSignIcon size={18} data-id="element-12022" data-mp="phquavxgsae0" />
                  </div>
                  <div data-id="element-12023" data-mp="pkmtenl1t541">
                    <p className="text-sm font-medium text-white" data-id="element-12024" data-mp="13hjhwidp2ahr">
                      {quote.customer}
                    </p>
                    <p className="text-xs text-blue-300/70" data-id="element-12025" data-mp="zfohrh7i235k">
                      {quote.id} • {quote.dealer}
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-12026" data-mp="188hzrmebzgn4">
                  <p className="text-sm font-bold text-white" data-id="element-12027" data-mp="18ausmulmnbey">
                    ${quote.amount.toLocaleString()}
                  </p>
                  <StatusBadge status={quote.status} data-id="element-12028" data-mp="1i77h9e2bez2u" />
                </div>
              </div>)}
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm" data-id="element-12029" data-mp="1uomko381py3b">
          <div className="flex items-center gap-2 mb-4 text-amber-400" data-id="element-12030" data-mp="1pre241izjs1j">
            <AlertCircleIcon size={20} data-id="element-12031" data-mp="23q3wvaic1fmq" />
            <h3 className="text-lg font-semibold text-white" data-id="element-12032" data-mp="23p2lt61bgt50">
              Needs Attention
            </h3>
          </div>
          <div className="space-y-3" data-id="element-12033" data-mp="1zpk2gep58q4a">
            {dummyQuotes.filter(q => q.needsAttention).map(quote => <div key={quote.id} className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20" data-id="element-12034" data-mp="1bzfvbxcqy88j">
                  <div className="flex justify-between items-start mb-1" data-id="element-12035" data-mp="16y0c8au4qbss">
                    <p className="text-sm font-medium text-white" data-id="element-12036" data-mp="1kt2p5hxe91f3">
                      {quote.customer}
                    </p>
                    <span className="text-xs text-amber-400 font-medium" data-id="element-12037" data-mp="1fyygmu8hclbw">
                      {quote.id}
                    </span>
                  </div>
                  <p className="text-xs text-amber-200/70 mb-2" data-id="element-12038" data-mp="o8k697zodr4z">
                    {quote.attentionReason}
                  </p>
                  <button className="text-xs bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 px-3 py-1.5 rounded transition-colors w-full font-medium" data-id="element-12039" data-mp="e3lph4ol97mx">
                    Review Quote
                  </button>
                </div>)}
            {dummyQuotes.filter(q => q.needsAttention).length === 0 && <p className="text-sm text-blue-300/50 text-center py-4" data-id="element-12040" data-mp="96v9sigp4e21">
                All caught up!
              </p>}
          </div>
        </div>
      </div>
    </div>;
}
function PipelineView() {
  const dummyQuotes = useQuotes();
  const columns: {
    title: string;
    statuses: QuoteStatus[];
  }[] = [{
    title: 'Draft',
    statuses: ['Draft']
  }, {
    title: 'Active',
    statuses: ['Active']
  }, {
    title: 'Expired',
    statuses: ['Expired']
  }, {
    title: 'Won',
    statuses: ['Won']
  }, {
    title: 'Lost',
    statuses: ['Lost']
  }, {
    title: 'Cancelled',
    statuses: ['Cancelled']
  }];
  return <div className="h-full flex flex-col" data-id="element-12041" data-mp="1j4ljji9axzdn">
      <div className="flex items-center justify-between mb-6" data-id="element-12042" data-mp="1j3a6ertyvf9v">
        <h2 className="text-xl font-bold text-white" data-id="element-12043" data-mp="1e5qva0skfim5">Sales Pipeline</h2>
        <div className="flex items-center gap-2" data-id="element-12044" data-mp="1e7lcp54moslh">
          <div className="relative" data-id="element-12045" data-mp="19dqlzhqlvdcw">
            <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-300/50" data-id="element-12046" data-mp="19cpajc5ogoun" />
            <input type="text" placeholder="Search quotes..." className="bg-white/5 border border-white/10 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-12047" data-mp="14dl24nqkuv5d" />
          </div>
          <button className="p-2 bg-white/5 border border-white/10 rounded-lg text-blue-300 hover:text-white hover:bg-white/10 transition-colors" data-id="element-12048" data-mp="qf4m266trsak">
            <FilterIcon size={18} data-id="element-12049" data-mp="lixpv548dv5t" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-x-auto pb-4 no-scrollbar" data-id="element-12050" data-mp="2ktfa4cfliifa">
        <div className="flex gap-4 min-w-max h-full" data-id="element-12051" data-mp="2fukt9g16v3xt">
          {columns.map(col => {
          const colQuotes = dummyQuotes.filter(q => col.statuses.includes(q.status));
          const colTotal = colQuotes.reduce((acc, q) => acc + q.amount, 0);
          return <div key={col.title} className="w-80 flex flex-col bg-white/5 border border-white/10 rounded-xl overflow-hidden flex-shrink-0" data-id="element-12052" data-mp="2sbdey3mu15c6">
                <div className="p-3 border-b border-white/10 bg-black/20 flex items-center justify-between" data-id="element-12053" data-mp="323j6xdd33mrd">
                  <h3 className="text-sm font-semibold text-white" data-id="element-12054" data-mp="24gkj7s4ddp5m">
                    {col.title}{' '}
                    <span className="text-blue-300/50 ml-1 font-normal" data-id="element-12055" data-mp="29fnfan2dgl1g">
                      ({colQuotes.length})
                    </span>
                  </h3>
                  <span className="text-xs font-medium text-blue-200" data-id="element-12056" data-mp="2jcibv0f34bd3">
                    ${(colTotal / 1000).toFixed(1)}k
                  </span>
                </div>
                <div className="flex-1 p-3 overflow-y-auto space-y-3 no-scrollbar" data-id="element-12057" data-mp="3puycdf05qk6u">
                  {colQuotes.map(quote => <div key={quote.id} className="bg-white/5 border border-white/10 rounded-lg p-3 hover:bg-white/10 transition-colors cursor-pointer group" data-id="element-12058" data-mp="3utrszgaqqrb8">
                      <div className="flex justify-between items-start mb-2" data-id="element-12059" data-mp="3oczyrmbll4h4">
                        <span className="text-xs font-medium text-blue-400" data-id="element-12060" data-mp="66bvm203yre3">
                          {quote.id}
                        </span>
                        <StatusBadge status={quote.status} data-id="element-12061" data-mp="191z62eaxfgr" />
                      </div>
                      <p className="text-sm font-semibold text-white mb-1" data-id="element-12062" data-mp="f4xczttug0da">
                        {quote.customer}
                      </p>
                      <p className="text-xs text-blue-300/70 mb-3" data-id="element-12063" data-mp="owa3oi3wokhq">
                        {quote.dealer}
                      </p>
                      <div className="flex items-center justify-between pt-2 border-t border-white/5" data-id="element-12064" data-mp="jyrssm6bvv73">
                        <span className="text-sm font-bold text-white" data-id="element-12065" data-mp="yviwv8qy5v31">
                          ${quote.amount.toLocaleString()}
                        </span>
                        <span className="text-xs font-medium text-blue-300 bg-blue-500/20 px-1.5 py-0.5 rounded" data-id="element-12066" data-mp="1dhpqdm8qifob">
                          {quote.probability}%
                        </span>
                      </div>
                    </div>)}
                </div>
              </div>;
        })}
        </div>
      </div>
    </div>;
}
// --- All Quotes Explorer (unified configurable view) ---
type GroupDimension = 'businessUnit' | 'salesManager' | 'dealer' | 'customer' | 'country' | 'status' | 'probability';
const DIMENSION_META: Record<GroupDimension, {
  label: string;
  icon: React.ReactNode;
  accessor: (q: Quote) => string;
}> = {
  businessUnit: {
    label: 'Business Unit',
    icon: <Building2Icon size={14} data-id="element-12067" data-mp="prvdj4of0r42" />,
    accessor: q => q.businessUnit
  },
  salesManager: {
    label: 'Sales Manager',
    icon: <UserIcon size={14} data-id="element-12068" data-mp="aubaw4ezr812" />,
    accessor: q => q.salesManager
  },
  dealer: {
    label: 'Dealer',
    icon: <BuildingIcon size={14} data-id="element-12069" data-mp="ftxpoiagwwbz" />,
    accessor: q => q.dealer
  },
  customer: {
    label: 'Customer',
    icon: <UsersIcon size={14} data-id="element-12070" data-mp="3svi9ymt2cfri" />,
    accessor: q => q.customer
  },
  country: {
    label: 'Country',
    icon: <GlobeIcon size={14} data-id="element-12071" data-mp="3o2ym8x0nw48z" />,
    accessor: q => q.country
  },
  status: {
    label: 'Status',
    icon: <CheckCircleIcon size={14} data-id="element-12072" data-mp="3j5f8dy7vlthk" />,
    accessor: q => q.status
  },
  probability: {
    label: 'Probability',
    icon: <TrendingUpIcon size={14} data-id="element-12073" data-mp="3e73wlhy0t8wp" />,
    accessor: q => {
      if (q.probability > 70) return 'High (>70%)';
      if (q.probability >= 30) return 'Medium (30–70%)';
      return 'Low (<30%)';
    }
  }
};
function getDefaultChain(role: string): GroupDimension[] {
  if (['ceo', 'system-admin'].includes(role)) return ['businessUnit', 'salesManager', 'dealer'];
  if (['manager-business-unit', 'region-admin'].includes(role)) return ['salesManager', 'dealer'];
  if (role === 'sales-manager') return ['dealer', 'customer'];
  if (['dealer', 'dealer-admin'].includes(role)) return ['customer'];
  return ['businessUnit'];
}
// Human-readable label per role for UI copy
function getRoleLabel(role: string): string {
  const map: Record<string, string> = {
    ceo: 'CEO',
    'system-admin': 'System Admin',
    'manager-business-unit': 'Business Unit Manager',
    'region-admin': 'Region Admin',
    'sales-manager': 'Sales Manager',
    dealer: 'Dealer',
    'dealer-admin': 'Dealer Admin',
    customer: 'Customer',
    'operations-manager': 'Operations Manager',
    logistic: 'Logistics',
    marketing: 'Marketing',
    'financial-manager': 'Financial Manager',
    'authenticated-public-user': 'Authenticated User'
  };
  return map[role] ?? role;
}
interface TreeGroup {
  key: string;
  label: string;
  dimension: GroupDimension;
  quotes: Quote[];
  children: TreeGroup[];
  totalAmount: number;
  openAmount: number;
  weighted: number;
  wonAmount: number;
  wonCount: number;
  lostCount: number;
  winRate: number;
  needsAttention: number;
}
function computeAggregates(quotes: Quote[]) {
  const closed: QuoteStatus[] = ['Won', 'Lost', 'Expired', 'Cancelled'];
  let totalAmount = 0;
  let openAmount = 0;
  let weighted = 0;
  let wonAmount = 0;
  let wonCount = 0;
  let lostCount = 0;
  let needsAttention = 0;
  quotes.forEach(q => {
    totalAmount += q.amount;
    if (!closed.includes(q.status)) {
      openAmount += q.amount;
      weighted += q.amount * q.probability / 100;
    }
    if (q.status === 'Won') {
      wonAmount += q.amount;
      wonCount += 1;
    }
    if (q.status === 'Lost' || q.status === 'Expired') lostCount += 1;
    if (q.needsAttention) needsAttention += 1;
  });
  const decided = wonCount + lostCount;
  const winRate = decided > 0 ? Math.round(wonCount / decided * 100) : 0;
  return {
    totalAmount,
    openAmount,
    weighted,
    wonAmount,
    wonCount,
    lostCount,
    winRate,
    needsAttention
  };
}
function buildTree(quotes: Quote[], chain: GroupDimension[], depth: number, parentPath: string): TreeGroup[] {
  if (depth >= chain.length) return [];
  const dim = chain[depth];
  const accessor = DIMENSION_META[dim].accessor;
  const buckets = new Map<string, Quote[]>();
  quotes.forEach(q => {
    const key = accessor(q);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(q);
  });
  const groups: TreeGroup[] = Array.from(buckets.entries()).map(([label, groupQuotes]) => {
    const path = `${parentPath}/${dim}:${label}`;
    const aggs = computeAggregates(groupQuotes);
    return {
      key: path,
      label,
      dimension: dim,
      quotes: groupQuotes,
      children: buildTree(groupQuotes, chain, depth + 1, path),
      ...aggs
    };
  });
  // Sort each level by openAmount desc by default
  return groups.sort((a, b) => b.openAmount - a.openAmount);
}
function GroupChainChip({
  dimension,
  onRemove,
  canRemove,
  index,
  isDragging,
  isDropTarget,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onDragLeave
}: {
  dimension: GroupDimension;
  onRemove: () => void;
  canRemove: boolean;
  index: number;
  isDragging: boolean;
  isDropTarget: boolean;
  onDragStart: (index: number) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDrop: (e: React.DragEvent, index: number) => void;
  onDragEnd: () => void;
  onDragLeave: () => void;
}) {
  const meta = DIMENSION_META[dimension];
  return <div draggable onDragStart={() => onDragStart(index)} onDragOver={e => onDragOver(e, index)} onDrop={e => onDrop(e, index)} onDragEnd={onDragEnd} onDragLeave={onDragLeave} className={`flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs font-medium cursor-grab active:cursor-grabbing select-none transition-all ${isDragging ? 'opacity-40 bg-blue-500/30 border-blue-400/60' : isDropTarget ? 'bg-blue-500/30 border-blue-300 ring-2 ring-blue-400/40 text-white' : 'bg-blue-500/15 border-blue-400/30 text-blue-100 hover:bg-blue-500/20'}`} data-id="element-12074" data-mp="20nucevtjp91z">
      <GripVerticalIcon size={12} className="text-blue-300/60 -ml-0.5" aria-hidden data-id="element-12075" data-mp="2od6ivnxhn30l" />
      <span className="text-blue-300" data-id="element-12076" data-mp="2tbq7xx7doux6">{meta.icon}</span>
      {meta.label}
      {canRemove && <button onClick={e => {
      e.stopPropagation();
      onRemove();
    }} onMouseDown={e => e.stopPropagation()} className="ml-0.5 text-blue-300/70 hover:text-white rounded-full hover:bg-blue-500/20 w-4 h-4 flex items-center justify-center" aria-label={`Remove ${meta.label} grouping`} data-id="element-12077" data-mp="hppe8nsohsin">
          ×
        </button>}
    </div>;
}
function AddLevelDropdown({
  available,
  onAdd
}: {
  available: GroupDimension[];
  onAdd: (d: GroupDimension) => void;
}) {
  const [open, setOpen] = useState(false);
  if (available.length === 0) return null;
  return <div className="relative" data-id="element-12078" data-mp="3q7lsk8uem1ez">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/5 border border-dashed border-white/20 text-blue-300 hover:bg-white/10 hover:text-white hover:border-blue-400/40 text-xs font-medium transition-colors" data-id="element-12079" data-mp="3l5mpl02kt6ik">
        <PlusIcon size={12} data-id="element-12080" data-mp="1jcfb1susesxq" />
        Add level
      </button>
      {open && <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} data-id="element-12081" data-mp="1edlx5zbl8zx1" />
          <div className="absolute top-full left-0 mt-1 z-50 w-48 bg-slate-900 border border-white/10 rounded-lg shadow-xl py-1" data-id="element-12082" data-mp="38uc7tw00w18r">
            {available.map(d => {
          const meta = DIMENSION_META[d];
          return <button key={d} onClick={() => {
            onAdd(d);
            setOpen(false);
          }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-blue-100 hover:bg-white/5 transition-colors text-left" data-id="element-12083" data-mp="2yzk8onqsl6bv">
                  <span className="text-blue-400" data-id="element-12084" data-mp="g3o7dfhaksq">{meta.icon}</span>
                  {meta.label}
                </button>;
        })}
          </div>
        </>}
    </div>;
}
// Status dot colors used across KPI breakdown and tree rows for consistency
const STATUS_DOT_COLORS: Record<QuoteStatus, string> = {
  Draft: 'bg-slate-400',
  Active: 'bg-blue-400',
  Expired: 'bg-rose-400',
  Won: 'bg-emerald-400',
  Lost: 'bg-red-400',
  Cancelled: 'bg-gray-400'
};
function TreeRow({
  node,
  depth,
  expanded,
  onToggle,
  isLast,
  statusesToShow,
  onOpenQuote,
  onQuoteAction,
  selectedQuoteId
}: {
  node: TreeGroup;
  depth: number;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  isLast: boolean;
  statusesToShow: QuoteStatus[];
  onOpenQuote?: (id: string) => void;
  onQuoteAction?: (id: string, action: 'view' | 'timeline' | 'notes') => void;
  selectedQuoteId?: string | null;
}) {
  const isOpen = expanded.has(node.key);
  const meta = DIMENSION_META[node.dimension];
  const hasChildren = node.children.length > 0;
  // When no children configured (we hit chain end), show quotes as leaves
  return <>
      <div onClick={() => onToggle(node.key)} className={`flex items-center gap-3 py-2.5 px-3 border-b border-white/5 transition-colors cursor-pointer ${depth === 0 ? 'bg-[#26386B]' : depth === 1 ? 'bg-[#2E4176]' : 'bg-[#374B82]'} hover:bg-[#3E538E]`} style={{
      paddingLeft: `${12 + depth * 20}px`
    }} data-id="element-12085" data-mp="1uo7i64taklxm">
        <ChevronRightIcon size={14} className={`flex-shrink-0 transition-transform ${isOpen ? 'rotate-90 text-blue-400' : 'text-blue-300/60'}`} data-id="element-12086" data-mp="x7s7i214hwvi" />
        <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-12087" data-mp="fdfdxxbkjgh">
          <span className="text-blue-400 flex-shrink-0" data-id="element-12088" data-mp="4jba7wfm383q">{meta.icon}</span>
          <div className="min-w-0" data-id="element-12089" data-mp="9gtnu04kqbi2">
            <p className="text-sm font-semibold text-white truncate" data-id="element-12090" data-mp="9gjt5z8l3g61">
              {node.label}
            </p>
            <p className="text-[10px] text-blue-300/50 uppercase tracking-wider font-medium" data-id="element-12091" data-mp="eafrj2srf51q">
              {meta.label}
              {node.needsAttention > 0 && <span className="ml-1.5 text-amber-400" data-id="element-12092" data-mp="o5ptjqj5rcfi">
                  · {node.needsAttention} alert
                  {node.needsAttention !== 1 ? 's' : ''}
                </span>}
            </p>
          </div>
        </div>
        {node.dimension === 'status' ? <div className="hidden md:flex items-center gap-5 flex-shrink-0 text-right" data-id="element-12093" data-mp="2tzqv7owkf0s6">
            <div className="w-14" data-id="element-12094" data-mp="2u23lqqge2jgu">
              <p className="text-[10px] uppercase tracking-wider text-blue-400/50 font-semibold" data-id="element-12095" data-mp="2yyal20ea4wpt">
                Total
              </p>
              <p className="text-sm font-bold text-white tabular-nums" data-id="element-12096" data-mp="2rgvthclr0a0m">
                {node.quotes.length}
              </p>
            </div>
            <div className="w-28" data-id="element-12097" data-mp="2hp88nujp5r84">
              <p className="text-[10px] uppercase tracking-wider text-blue-400/50 font-semibold" data-id="element-12098" data-mp="2hqj9blhuiiv0">
                Importe Total
              </p>
              <p className="text-sm font-bold text-white tabular-nums" data-id="element-12099" data-mp="2cok8ojdhq01r">
                ${node.totalAmount.toLocaleString()}
              </p>
            </div>
          </div> : <div className="hidden md:flex items-center gap-5 flex-shrink-0 text-right" data-id="element-12100" data-mp="35flq6r4no7og">
            <div className="w-14" data-id="element-12101" data-mp="30eoaqmb15rqn">
              <p className="text-[10px] uppercase tracking-wider text-blue-400/50 font-semibold" data-id="element-12102" data-mp="30hu104veazny">
                Total
              </p>
              <p className="text-sm font-bold text-white tabular-nums" data-id="element-12103" data-mp="1p1c5q83jb32w">
                {node.quotes.length}
              </p>
            </div>
            {statusesToShow.map(s => {
          const matching = node.quotes.filter(q => q.status === s);
          const count = matching.length;
          const amount = matching.reduce((acc, q) => acc + q.amount, 0);
          return <div key={s} className="w-24" data-id="element-12104" data-mp="1ci8et1x57r5g">
                  {/* Level 1 — status label + count (count is the prominent value) */}
                  <p className="text-[10px] uppercase tracking-wider text-blue-400/50 font-semibold flex items-center justify-end gap-1" data-id="element-12105" data-mp="1hgiaddms4trl">
                    <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT_COLORS[s]}`} data-id="element-12106" data-mp="13olmjls59610" />
                    {s}
                    <span className="text-sm text-white font-bold tabular-nums normal-case tracking-normal ml-1" data-id="element-12107" data-mp="18ig3wika18wx">
                      {count}
                    </span>
                  </p>
                  {/* Level 2 — amount, de-emphasized so it doesn't compete with the count */}
                  <p className="text-[11px] text-blue-300/70 tabular-nums" data-id="element-12108" data-mp="kt5ajm59agst">
                    ${amount.toLocaleString()}
                  </p>
                </div>;
        })}
            <div className="w-28" data-id="element-12109" data-mp="654xwtvo5jin">
              <p className="text-[10px] uppercase tracking-wider text-blue-400/50 font-semibold" data-id="element-12110" data-mp="666jhsab9oem">
                Importe Total
              </p>
              <p className="text-sm font-bold text-white tabular-nums" data-id="element-12111" data-mp="f632pedxlv2a">
                ${node.totalAmount.toLocaleString()}
              </p>
            </div>
          </div>}
      </div>
      {isOpen && hasChildren && <div data-id="element-12112" data-mp="p1wo7n9moyw7">
          {node.children.map((child, i) => <TreeRow key={child.key} node={child} depth={depth + 1} expanded={expanded} onToggle={onToggle} isLast={i === node.children.length - 1} statusesToShow={statusesToShow} onOpenQuote={onOpenQuote} onQuoteAction={onQuoteAction} selectedQuoteId={selectedQuoteId} data-id="element-12113" data-mp="k44m60f9rwea" data-mpi={i} />)}
        </div>}
      {isOpen && !hasChildren && <div data-id="element-12114" data-mp="17tfovng6k0v2">
          {node.quotes.map(q => <QuoteLeafRow key={q.id} quote={q} depth={depth + 1} onOpen={onOpenQuote} onAction={onQuoteAction} isSelected={q.id === selectedQuoteId} data-id="element-12115" data-mp="17uh1hwcugvew" />)}
        </div>}
    </>;
}
function QuoteLeafRow({
  quote,
  depth,
  onOpen,
  onAction,
  isSelected
}: {
  quote: Quote;
  depth: number;
  onOpen?: (id: string) => void;
  onAction?: (id: string, action: 'view' | 'timeline' | 'notes') => void;
  isSelected?: boolean;
}) {
  const productType = getProductType(quote);
  const productCount = getProductCount(quote);
  const productLabel = PRODUCT_TYPE_LABEL[productType];
  const expiresAt = getExpiresAt(quote);
  const now = new Date();
  const expDate = new Date(expiresAt);
  const isExpiringSoon = !isNaN(expDate.getTime()) && expDate.getTime() - now.getTime() < 1000 * 60 * 60 * 24 * 7 && !['Won', 'Lost', 'Cancelled', 'Expired'].includes(quote.status);
  const noteAvailable = hasNote(quote);
  const noteColor = quote.status === 'Lost' || quote.status === 'Cancelled' ? 'text-rose-300 hover:text-rose-200' : quote.status === 'Won' ? 'text-emerald-300 hover:text-emerald-200' : 'text-blue-300 hover:text-blue-200';
  const [menuOpen, setMenuOpen] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  // Auto-scroll into view when this row becomes selected (e.g. via prev/next in drawer)
  useEffect(() => {
    if (isSelected && rowRef.current) {
      rowRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [isSelected]);
  return <div ref={rowRef} role={onOpen ? 'button' : undefined} tabIndex={onOpen ? 0 : undefined} onClick={onOpen ? () => onOpen(quote.id) : undefined} onKeyDown={onOpen ? e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen(quote.id);
    }
  } : undefined} className={`flex items-center gap-3 py-2 px-3 border-b border-white/5 transition-colors ${onOpen ? 'cursor-pointer' : ''} ${isSelected ? 'bg-[#455C9C] border-l-2 border-l-blue-300' : 'bg-[#3F5490] hover:bg-[#455C9C]'}`} style={{
    paddingLeft: `${12 + depth * 20 - (isSelected ? 2 : 0)}px`
  }} data-id="element-12116" data-mp="qa2vqf3nbnk0">
      {/* ── GROUP 1 · Identidad principal — status + code/name + dealer/customer.
                            Takes the lion's share of the width and truncates with "…" rather than
                            letting columns disappear when space is tight. ── */}
      <div className="flex items-center gap-3 flex-[4] min-w-0" data-id="element-12117" data-mp="1hqkq6azel5my">
        {/* 1a. Status badge (auto width so code/name sits right beside it) */}
        <div className="flex-shrink-0 flex items-center" data-id="element-12118" data-mp="1dqjxpmfgytww">
          <StatusBadge status={quote.status} data-id="element-12119" data-mp="2af9hxjekjym1" />
        </div>
        {/* 1b. Quote code (top) + Quote name (bottom) — flexes & truncates */}
        <div className="flex-[1.4] min-w-[90px]" data-id="element-12120" data-mp="25hq24f283drz">
          <p className="text-xs font-mono text-blue-400 truncate" data-id="element-12121" data-mp="21el3inl9jw8m">{quote.id}</p>
          <p className="text-sm text-white font-medium truncate" data-id="element-12122" data-mp="21go421k1l7rl">
            {getQuoteName(quote)}
          </p>
        </div>
        {/* 1c. Dealer & Customer — always visible, flexes & truncates. Given
              extra weight so it wins the space freed from "Quoted by". */}
        <div className="flex-[1.3] min-w-[90px] border-l border-white/5 pl-3" data-id="element-12123" data-mp="kza1ricj98ww">
          {quote.dealer && quote.customer ? <>
              <p className="text-sm text-white font-medium truncate" data-id="element-12124" data-mp="31n1btgx4ymjg">
                {quote.dealer}
              </p>
              <p className="text-[11px] text-blue-300/60 truncate" data-id="element-12125" data-mp="35o3b1xr1yylf">
                Customer: {quote.customer}
              </p>
            </> : quote.dealer ? <>
              <p className="text-sm text-white font-medium truncate" data-id="element-12126" data-mp="3ah68wi2v4u7f">
                {quote.dealer}
              </p>
              <p className="text-[11px] text-blue-300/50 truncate" data-id="element-12127" data-mp="3kd99hji2gks9">Dealer</p>
            </> : <>
              <p className="text-sm text-white font-medium truncate" data-id="element-12128" data-mp="3pcvmbrtzfz0j">
                {quote.customer}
              </p>
              <p className="text-[11px] text-blue-300/50 truncate" data-id="element-12129" data-mp="3teh67x4xpdk8">
                Direct customer
              </p>
            </>}
        </div>
      </div>

      {/* ── Metadata groups (scope + cycle + quoted-by + indicators) — kept on one
                            line, each shrunk to a tight minimum so nothing disappears. ── */}
      <div className="flex items-center gap-2 flex-shrink-0" data-id="element-12130" data-mp="32blwb5j5a1s5">
        {/* GROUP 2 · Alcance — type + quantity summary */}
        <div className="w-20 flex-shrink-0" data-id="element-12131" data-mp="2xec0n855wtvv">
          <p className="text-[11px] font-semibold text-blue-100 truncate" data-id="element-12132" data-mp="3bc0jybjkzsyt">
            {productLabel}
          </p>
          <p className="text-[11px] text-blue-300/60 tabular-nums" data-id="element-12133" data-mp="379dsibqtoa25">
            {productCount} {productCount === 1 ? 'unit' : 'units'}
          </p>
        </div>

        {/* GROUP 3 · Ciclo comercial — created + expires (narrowed so the gap
              to "Quoted by" shrinks) */}
        <div className="w-32 flex-shrink-0" data-id="element-12134" data-mp="3uypw8w3drpx2">
          <p className="text-[11px] text-blue-300/60 tabular-nums truncate" data-id="element-12135" data-mp="3v1ubqpmaq831">
            Created {formatShortDate(quote.date)}
          </p>
          {(() => {
          const isExpired = quote.status === 'Expired';
          const daysExpired = isExpired ? Math.max(0, Math.floor((now.getTime() - expDate.getTime()) / (1000 * 60 * 60 * 24))) : 0;
          const cls = isExpired ? 'text-rose-300' : isExpiringSoon ? 'text-amber-300' : 'text-blue-300/60';
          return <p className={`text-[11px] tabular-nums ${cls}`} data-id="element-12136" data-mp="rfio6ofqpnqp">
                Expires {formatShortDate(expiresAt)}
                {isExpired && <span className="ml-1 text-rose-400/80 font-medium" data-id="element-12137" data-mp="dj54d8nqha92">
                    ({daysExpired} {daysExpired === 1 ? 'day' : 'days'} ago)
                  </span>}
              </p>;
        })()}
        </div>
        {/* GROUP 3 · Quoted by — narrowed so its space goes to code/name & dealer */}
        <div className="w-20 flex-shrink-0 min-w-0" data-id="element-12138" data-mp="8k2beml12uhg">
          <p className="text-[10px] uppercase tracking-wider text-blue-400/50 font-semibold" data-id="element-12139" data-mp="3pzij6uedjvdu">
            Quoted by
          </p>
          <p className="text-xs text-blue-100 truncate" data-id="element-12140" data-mp="3uxttf68l07wl">{getQuotedBy(quote)}</p>
        </div>

        {/* GROUP 4 · Indicadores comerciales — probability + amount + notes.
              Probability & amount pulled tighter together (gap-2). */}
        <div className="flex items-center gap-2 border-l border-white/5 pl-3" data-id="element-12141" data-mp="3dvwl37fgnvkc">
          <div className="w-14 flex-shrink-0 flex justify-end" data-id="element-12142" data-mp="3dy0ta1ke7gqf">
            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded border tabular-nums ${quote.probability > 70 ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : quote.probability >= 30 ? 'bg-amber-500/20 text-amber-300 border-amber-500/30' : 'bg-rose-500/20 text-rose-300 border-rose-500/30'}`} title="Win probability" data-id="element-12143" data-mp="38xmr305h310b">
              {quote.probability}%
            </span>
          </div>
          <div className="w-20 text-right flex-shrink-0" data-id="element-12144" data-mp="2q5agjq4o0tf3">
            <p className="text-sm text-white font-semibold tabular-nums" data-id="element-12145" data-mp="2l6qulpq2nhmq">
              ${quote.amount.toLocaleString()}
            </p>
          </div>
          {/* Notes icon lives in the former alert slot. Alerts are not
           implemented yet, so this replaces the orange attention icon. */}
          <div className="w-6 flex-shrink-0 flex justify-center" data-id="element-12146" data-mp="feambdn8t66c">
            {noteAvailable && <button type="button" onClick={e => {
            e.stopPropagation();
            onAction?.(quote.id, 'notes');
          }} className={`flex-shrink-0 ${noteColor} transition-colors`} title="View notes" aria-label={`View notes for ${quote.id}`} data-id="element-12147" data-mp="1xcp2e4tasjjv">
                <FileTextIcon size={14} data-id="element-12148" data-mp="27436yr03w9jv" />
              </button>}
          </div>
        </div>
      </div>

      {/* ── GROUP 5 · Acciones — always pinned right & visible (ml-auto) ── */}
      <div className="ml-auto flex-shrink-0 flex items-center gap-1" data-id="element-12149" data-mp="1ef5revna4qut">
        <button type="button" onClick={e => {
        e.stopPropagation();
        onAction?.(quote.id, 'view');
      }} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-blue-600/20 hover:bg-blue-600/40 text-blue-300 hover:text-blue-200 transition-colors text-xs font-medium" data-id="element-12150" data-mp="1jdfl11tcvjeb">
          <EyeIcon size={12} data-id="element-12151" data-mp="2ys4p979fvd00" />
          <span className="hidden sm:inline" data-id="element-12152" data-mp="2yswmsnmljksx">View</span>
        </button>
        <div className="relative" data-id="element-12153" data-mp="33r7x0zgszxbo">
          <button type="button" onClick={e => {
          e.stopPropagation();
          setMenuOpen(o => !o);
        }} aria-label="Quote actions" aria-haspopup="menu" aria-expanded={menuOpen} className="w-7 h-7 flex items-center justify-center rounded text-blue-300 hover:text-white hover:bg-white/10 transition-colors" title="Actions" data-id="element-12154" data-mp="33uc8mhwllvcd">
            <MoreVerticalIcon size={14} data-id="element-12155" data-mp="18okzvk5sb910" />
          </button>
          {menuOpen && <>
              <div className="fixed inset-0 z-30" onClick={e => {
            e.stopPropagation();
            setMenuOpen(false);
          }} data-id="element-12156" data-mp="kz8qokagj0yp" />
              <div className="absolute right-0 top-full mt-1 z-40 w-40 bg-slate-900 border border-white/10 rounded-md shadow-lg overflow-hidden" data-id="element-12157" data-mp="g206yf9nuovc">
                {[{
              action: 'timeline' as const,
              label: 'Timeline',
              icon: <ClockIcon size={12} data-id="element-12158" data-mp="28r1uqsqwpim" />
            }, {
              action: 'notes' as const,
              label: 'Notes',
              icon: <MessageSquareIcon size={12} data-id="element-12159" data-mp="3teacdqj0miqj" />
            }].map(item => <button key={item.action} type="button" onClick={e => {
              e.stopPropagation();
              setMenuOpen(false);
              onAction?.(quote.id, item.action);
            }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-blue-100 hover:bg-white/10 hover:text-white transition-colors text-left" data-id="element-12160" data-mp="28x9j3zu7yuke">
                    <span className="text-blue-300/70 flex-shrink-0" data-id="element-12161" data-mp="23z7xp89bmj5r">
                      {item.icon}
                    </span>
                    {item.label}
                  </button>)}
              </div>
            </>}
        </div>
      </div>
    </div>;
}
interface QuoteFilters {
  status: QuoteStatus[];
  businessUnit: string[];
  salesManager: string[];
  country: string[];
  dealer: string[];
  probabilityBucket: ('high' | 'medium' | 'low')[];
  quotedByMe: boolean;
}
// --- Date Range ---
type DateRangeMode = 'last12m' | 'year' | 'month';
interface DateRange {
  mode: DateRangeMode;
  year: number;
  month?: number; // 1-12 when mode === 'month'
}
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DEFAULT_DATE_RANGE: DateRange = {
  mode: 'last12m',
  year: new Date().getFullYear()
};
function isDefaultDateRange(r: DateRange): boolean {
  return r.mode === 'last12m';
}
function formatDateRangeLabel(r: DateRange): string {
  if (r.mode === 'last12m') return 'Last 12 months';
  if (r.mode === 'year') return `${r.year}`;
  if (r.mode === 'month' && r.month) return `${MONTH_NAMES[r.month - 1]} ${r.year}`;
  return '';
}
function quoteMatchesDateRange(q: Quote, r: DateRange): boolean {
  const d = new Date(q.date);
  if (isNaN(d.getTime())) return true;
  if (r.mode === 'last12m') {
    const cutoff = new Date();
    cutoff.setFullYear(cutoff.getFullYear() - 1);
    return d >= cutoff;
  }
  if (r.mode === 'year') return d.getFullYear() === r.year;
  if (r.mode === 'month' && r.month) return d.getFullYear() === r.year && d.getMonth() + 1 === r.month;
  return true;
}
// --- Sorting ---
type SortKey = 'id' | 'date' | 'expiresAt' | 'amount' | 'probability' | 'customer' | 'dealer' | 'quotedBy' | 'productCount';
type SortDir = 'asc' | 'desc';
const SORT_OPTIONS: {
  key: SortKey;
  label: string;
}[] = [{
  key: 'date',
  label: 'Created date'
}, {
  key: 'expiresAt',
  label: 'Expires date'
}, {
  key: 'amount',
  label: 'Amount'
}, {
  key: 'id',
  label: 'Code'
}, {
  key: 'customer',
  label: 'Customer'
}, {
  key: 'dealer',
  label: 'Dealer'
}, {
  key: 'quotedBy',
  label: 'Quoted by'
}, {
  key: 'productCount',
  label: 'Product count'
}, {
  key: 'probability',
  label: 'Probability'
}];
function sortQuotes(quotes: Quote[], {
  key,
  dir
}: {
  key: SortKey;
  dir: SortDir;
}): Quote[] {
  const mul = dir === 'asc' ? 1 : -1;
  const getValue = (q: Quote): number | string => {
    switch (key) {
      case 'id':
        return q.id;
      case 'date':
        return new Date(q.date).getTime();
      case 'expiresAt':
        return new Date(getExpiresAt(q)).getTime();
      case 'amount':
        return q.amount;
      case 'probability':
        return q.probability;
      case 'customer':
        return q.customer;
      case 'dealer':
        return q.dealer;
      case 'quotedBy':
        return getQuotedBy(q);
      case 'productCount':
        return getProductCount(q);
    }
  };
  return [...quotes].sort((a, b) => {
    const va = getValue(a);
    const vb = getValue(b);
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mul;
    return String(va).localeCompare(String(vb)) * mul;
  });
}
// --- Export to CSV (Excel-compatible) ---
function exportQuotesToCsv(quotes: Quote[]) {
  const headers = ['Code', 'Name', 'Status', 'Customer', 'Dealer', 'Quoted by', 'Business Unit', 'Sales Manager', 'Country', 'Product Type', 'Product Count', 'Amount', 'Probability', 'Created', 'Expires', 'Last Updated'];
  const rows = quotes.map(q => [q.id, getQuoteName(q), q.status, q.customer, q.dealer, getQuotedBy(q), q.businessUnit, q.salesManager, q.country, PRODUCT_TYPE_LABEL[getProductType(q)], getProductCount(q), q.amount, q.probability, q.date, getExpiresAt(q), q.lastUpdated]);
  const escape = (v: string | number) => {
    const s = String(v ?? '');
    if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };
  const csv = [headers, ...rows].map(r => r.map(escape).join(',')).join('\n');
  // BOM so Excel detects UTF-8 properly
  const blob = new Blob(['\uFEFF' + csv], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `quotes-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
const EMPTY_FILTERS: QuoteFilters = {
  status: [],
  businessUnit: [],
  salesManager: [],
  country: [],
  dealer: [],
  probabilityBucket: [],
  quotedByMe: false
};
// Initial filters applied when the Explorer first loads (user can still Clear all)
const INITIAL_FILTERS: QuoteFilters = {
  ...EMPTY_FILTERS,
  status: ['Active', 'Expired']
};
function FilterAccordionSection({
  label,
  options,
  selected,
  onChange,
  defaultOpen = false
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const toggleOption = (opt: string) => {
    if (selected.includes(opt)) {
      onChange(selected.filter(s => s !== opt));
    } else {
      onChange([...selected, opt]);
    }
  };
  // Sort: selected first, then unselected
  const sorted = [...options.filter(o => selected.includes(o)), ...options.filter(o => !selected.includes(o))];
  const summary = selected.length === 0 ? 'All' : selected.length === 1 ? selected[0] : `${selected.length} selected`;
  return <div className="border border-white/5 rounded-md bg-white/5 overflow-hidden" data-id="element-12162" data-mp="38yz6937h46w2">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-white/5 transition-colors" data-id="element-12163" data-mp="31fzmgwg3odc6">
        <div className="min-w-0 flex-1" data-id="element-12164" data-mp="2why82otmd271">
          <p className="text-xs text-[#6A85A8] font-semibold" data-id="element-12165" data-mp="2sf1sz83afo7u">{label}</p>
          <p className={`text-xs mt-0.5 truncate ${selected.length > 0 ? 'text-white font-medium' : 'text-blue-300/60'}`} data-id="element-12166" data-mp="2sheajk54vwkd">
            {summary}
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-12167" data-mp="2iiyjfh63wnav">
          {selected.length > 0 && <button onClick={e => {
          e.stopPropagation();
          onChange([]);
        }} className="text-blue-300/60 hover:text-white p-0.5 rounded hover:bg-white/10" aria-label={`Clear ${label} filter`} data-id="element-12168" data-mp="2dp3zqdqi48bs">
              <XIcon size={12} data-id="element-12169" data-mp="24nw1twrwd9i8" />
            </button>}
          <ChevronDownIcon size={14} className={`text-blue-300/60 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-12170" data-mp="1zr5mjbnx2hxv" />
        </div>
      </button>
      {open && <div className="border-t border-white/5 max-h-44 overflow-y-auto no-scrollbar" data-id="element-12171" data-mp="1utvyo0g6eec5">
          {sorted.length === 0 ? <p className="text-xs text-blue-300/40 italic px-3 py-2" data-id="element-12172" data-mp="3nrgt5p4jixz">
              No options available
            </p> : sorted.map(opt => {
        const isSelected = selected.includes(opt);
        return <button key={opt} onClick={() => toggleOption(opt)} className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-white/5 transition-colors" data-id="element-12173" data-mp="3q3c7fa6upqnr">
                  <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-blue-500 border-blue-400' : 'border-white/20 bg-white/5'}`} data-id="element-12174" data-mp="3c29i8okk808m">
                    {isSelected && <CheckCircleIcon size={10} className="text-white" data-id="element-12175" data-mp="327q09kehiwsq" />}
                  </div>
                  <span className={`text-xs truncate ${isSelected ? 'text-white font-medium' : 'text-blue-200'}`} data-id="element-12176" data-mp="376azphlwxcg4">
                    {opt}
                  </span>
                </button>;
      })}
        </div>}
    </div>;
}
// --- KPI customization (Phase 1) ---
type KpiId = 'totalQuotes' | 'totalAmount' | 'statusBreakdown' | 'needsAttention';
const KPI_META: Record<KpiId, {
  label: string;
}> = {
  totalQuotes: {
    label: 'Total Quotes'
  },
  totalAmount: {
    label: 'Importe Total'
  },
  statusBreakdown: {
    label: 'Status (dynamic)'
  },
  needsAttention: {
    label: 'Needs Attention'
  }
};
const ALL_KPIS: KpiId[] = ['totalQuotes', 'totalAmount', 'statusBreakdown', 'needsAttention'];
interface KpiPrefs {
  showKpis: boolean;
  visibleKpis: KpiId[];
}
function getDefaultKpiPrefs(_role: string): KpiPrefs {
  // All roles see the 4 KPIs by default; users can still customize.
  return {
    showKpis: true,
    visibleKpis: ['totalQuotes', 'totalAmount', 'statusBreakdown', 'needsAttention']
  };
}
// --- Quote Notes Panel (chat-like internal communication) ---
interface ChatMessage {
  id: string;
  author: string;
  role: 'sales-manager' | 'dealer' | 'operations' | 'bu-manager' | 'customer';
  // For dealer messages: the dealer company the author belongs to. A single
  // dealer company can have several users managing a quote, so we surface the
  // specific user (author) plus their company here.
  company?: string;
  date: string;
  text: string;
  isChangeRequest?: boolean;
}
// A dealer company has multiple users — pick stable "user" names per quote so
// dealer messages show WHO wrote them, not just the company.
const DEALER_USERS = ['Andriy Kovalenko', 'Olena Bondar', 'Pavlo Shevchenko', 'Natalia Tkachenko', 'Serhiy Melnyk'];
function getNotesThread(q: Quote): ChatMessage[] {
  const seed = hashCode(q.id);
  const sm = q.salesManager || 'Sales Manager';
  const dealerCompany = q.dealer || 'Dealer';
  // Two different dealer users on the same company thread
  const dealerUserA = DEALER_USERS[Math.abs(seed) % DEALER_USERS.length];
  const dealerUserB = DEALER_USERS[(Math.abs(seed) + 2) % DEALER_USERS.length];
  const baseDate = new Date(q.date);
  const dayOffset = (n: number) => {
    const d = new Date(baseDate);
    d.setDate(d.getDate() + n);
    return d.toISOString();
  };
  // Realistic templates rotated by seed
  const templates: ChatMessage[][] = [[{
    id: 'm1',
    author: dealerUserA,
    role: 'dealer',
    company: dealerCompany,
    date: dayOffset(1),
    text: 'Customer wants a 12% discount to close. Can we authorise?',
    isChangeRequest: true
  }, {
    id: 'm2',
    author: sm,
    role: 'sales-manager',
    date: dayOffset(2),
    text: 'Max we can offer is 8% given current margin. Try bundling the service plan instead — that often closes the gap.'
  }, {
    id: 'm3',
    author: dealerUserB,
    role: 'dealer',
    company: dealerCompany,
    date: dayOffset(3),
    text: 'Customer also asked if we can switch to 8" pipe instead of 6". Need updated quote.',
    isChangeRequest: true
  }], [{
    id: 'm1',
    author: dealerUserA,
    role: 'dealer',
    company: dealerCompany,
    date: dayOffset(2),
    text: 'Customer needs delivery before May 15. Is that feasible?',
    isChangeRequest: true
  }, {
    id: 'm2',
    author: 'Operations',
    role: 'operations',
    date: dayOffset(3),
    text: 'Current lead time is 6 weeks. We could expedite if customer pays 3% rush fee.'
  }, {
    id: 'm3',
    author: sm,
    role: 'sales-manager',
    date: dayOffset(4),
    text: 'Let me know if they accept the rush fee. Otherwise we ship by June 1.'
  }], [{
    id: 'm1',
    author: dealerUserA,
    role: 'dealer',
    company: dealerCompany,
    date: dayOffset(1),
    text: 'Customer reviewed quote and wants to add 2 more pivots to the order.',
    isChangeRequest: true
  }, {
    id: 'm2',
    author: sm,
    role: 'sales-manager',
    date: dayOffset(2),
    text: 'Great — I’ll prepare an updated quote. Bulk pricing should kick in at 5+ units.'
  }]];
  return templates[Math.abs(seed) % templates.length];
}
function roleColor(role: ChatMessage['role']) {
  switch (role) {
    case 'sales-manager':
      return 'bg-blue-500/20 text-blue-200 border-blue-400/30';
    case 'dealer':
      return 'bg-amber-500/20 text-amber-200 border-amber-400/30';
    case 'operations':
      return 'bg-indigo-500/20 text-indigo-200 border-indigo-400/30';
    case 'bu-manager':
      return 'bg-purple-500/20 text-purple-200 border-purple-400/30';
    case 'customer':
      return 'bg-emerald-500/20 text-emerald-200 border-emerald-400/30';
  }
}
function roleLabel(role: ChatMessage['role']) {
  switch (role) {
    case 'sales-manager':
      return 'Sales Manager';
    case 'dealer':
      return 'Dealer';
    case 'operations':
      return 'Operations';
    case 'bu-manager':
      return 'BU Manager';
    case 'customer':
      return 'Customer';
  }
}
function QuoteNotesPanel({
  quote
}: {
  quote: Quote;
}) {
  const {
    name: currentUser
  } = useUser();
  const [draft, setDraft] = useState('');
  const [extra, setExtra] = useState<ChatMessage[]>([]);
  const baseThread = useMemo(() => getNotesThread(quote), [quote.id]);
  const messages = useMemo(() => [...baseThread, ...extra].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()), [baseThread, extra]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const submit = () => {
    const text = draft.trim();
    if (!text) return;
    setExtra(arr => [...arr, {
      id: `local-${Date.now()}`,
      author: currentUser || 'You',
      role: 'sales-manager',
      date: new Date().toISOString(),
      text
    }]);
    setDraft('');
  };
  // Keep the chat scrolled to the latest message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);
  const initials = (name: string) => name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const isMine = (m: ChatMessage) => m.author === (currentUser || 'You');
  return <section className="flex flex-col h-full" data-id="element-12177" data-mp="j8bctve9cj72">
      <div className="px-4 py-2.5 border-b border-white/5 flex-shrink-0" data-id="element-12178" data-mp="j9mglv7pdqy6">
        <p className="text-[11px] text-blue-300/70 leading-relaxed" data-id="element-12179" data-mp="2uwg65ajtpyg2">
          Internal thread between sales manager, dealer and operations. Use this
          to track change requests and decisions.
        </p>
      </div>
      {/* Chat thread */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 space-y-4" data-id="element-12180" data-mp="2l3r5zbtq4701">
        {messages.length === 0 ? <p className="text-xs text-blue-300/60 italic text-center py-6" data-id="element-12181" data-mp="2c56t4raugrjq">
            No messages yet — start the conversation below.
          </p> : messages.map(m => {
        const mine = isMine(m);
        return <div key={m.id} className={`flex items-end gap-2 ${mine ? 'flex-row-reverse' : 'flex-row'}`} data-id="element-12182" data-mp="22794w3sziur4">
                {/* Avatar */}
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${mine ? 'bg-blue-500/30 text-blue-100' : 'bg-white/10 text-blue-200'}`} title={m.author} data-id="element-12183" data-mp="276cb3rkpb70o">
                  {initials(m.author)}
                </div>
                {/* Bubble + meta */}
                <div className={`flex flex-col max-w-[78%] min-w-0 ${mine ? 'items-end' : 'items-start'}`} data-id="element-12184" data-mp="3v7dd65hvqva">
                  <div className={`flex items-center gap-1.5 mb-1 flex-wrap ${mine ? 'flex-row-reverse' : ''}`} data-id="element-12185" data-mp="3v5xzn16fermc">
                    <span className="text-[11px] font-medium text-white truncate" data-id="element-12186" data-mp="3m2wvv7519clu">
                      {mine ? 'You' : m.author}
                    </span>
                    <span className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border ${roleColor(m.role)}`} data-id="element-12187" data-mp="mn9zuy4ti1dg">
                      {roleLabel(m.role)}
                    </span>
                    {m.role === 'dealer' && m.company && <span className="text-[9px] text-amber-300/70 font-medium truncate max-w-[140px]" data-id="element-12188" data-mp="8wmyey7o9xdo">
                        · {m.company}
                      </span>}
                    {m.isChangeRequest && <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-400/30" data-id="element-12189" data-mp="10nkrw4hv5m07">
                        Change request
                      </span>}
                  </div>
                  <div className={`px-3 py-2 text-sm leading-relaxed break-words ${mine ? 'bg-blue-600 text-white rounded-2xl rounded-br-sm' : 'bg-white/10 text-blue-100 border border-white/10 rounded-2xl rounded-bl-sm'}`} data-id="element-12190" data-mp="2o9xgonqhet23">
                    {m.text}
                  </div>
                  <span className="text-[10px] text-blue-300/50 tabular-nums mt-1 px-1" data-id="element-12191" data-mp="3283ykuf1cig4">
                    {formatLongDate(m.date)}
                  </span>
                </div>
              </div>;
      })}
      </div>
      {/* Composer */}
      <div className="px-3 py-3 border-t border-white/10 flex-shrink-0 bg-slate-900" data-id="element-12192" data-mp="3c12xp685rrdz">
        <div className="flex items-end gap-2 bg-white/5 border border-white/10 rounded-2xl px-3 py-2 focus-within:ring-1 focus-within:ring-blue-400 transition-shadow" data-id="element-12193" data-mp="3fzs8cr1tautx">
          <textarea value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }} placeholder="Write a message…" rows={3} className="flex-1 resize-none bg-transparent text-sm text-white placeholder-blue-300/50 focus:outline-none min-h-[72px] max-h-48 no-scrollbar leading-relaxed" data-id="element-12194" data-mp="3pzsusluz9r2c" />
          <button onClick={submit} disabled={!draft.trim()} className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 hover:bg-blue-700 disabled:bg-white/5 disabled:text-blue-300/30 text-white transition-colors" aria-label="Send message" title="Send (Enter)" data-id="element-12195" data-mp="3h396dwbe7id9">
            <SendIcon size={14} data-id="element-12196" data-mp="33cx9lk3v4vfs" />
          </button>
        </div>
        <p className="text-[10px] text-blue-300/40 mt-1.5 px-1" data-id="element-12197" data-mp="2ybzujge5ghii">
          Press Enter to send · Shift+Enter for a new line
        </p>
      </div>
    </section>;
}
// --- Quote Detail Drawer ---
function QuoteDetailDrawer({
  quote,
  mode,
  onModeChange,
  onClose,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  position,
  total
}: {
  quote: Quote | null;
  mode: 'summary' | 'timeline' | 'notes';
  onModeChange: (m: 'summary' | 'timeline' | 'notes') => void;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
  position: number;
  total: number;
}) {
  useEffect(() => {
    if (!quote) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrev) onPrev();
      if (e.key === 'ArrowRight' && hasNext) onNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [quote, hasPrev, hasNext, onClose, onPrev, onNext]);
  if (!quote) return null;
  const closureNote = getClosureNote(quote);
  const timeline = getTimeline(quote);
  const expiresAt = getExpiresAt(quote);
  const productType = getProductType(quote);
  const productCount = getProductCount(quote);
  const isClosed = ['Lost', 'Cancelled', 'Won', 'Expired'].includes(quote.status);
  const closureBorder = quote.status === 'Lost' || quote.status === 'Cancelled' ? 'border-rose-500/30 bg-rose-500/5' : quote.status === 'Won' ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-blue-500/30 bg-blue-500/5';
  const closureIcon = quote.status === 'Lost' || quote.status === 'Cancelled' ? 'text-rose-300' : quote.status === 'Won' ? 'text-emerald-300' : 'text-blue-300';
  const timelineDotColor = (kind: ActivityKind, status?: QuoteStatus) => {
    if (kind === 'status-change') {
      if (status === 'Won') return 'bg-emerald-400';
      if (status === 'Lost' || status === 'Cancelled') return 'bg-rose-400';
      if (status === 'Expired') return 'bg-amber-400';
    }
    if (kind === 'note') return 'bg-blue-400';
    if (kind === 'discount') return 'bg-amber-400';
    if (kind === 'items-changed') return 'bg-indigo-400';
    if (kind === 'created') return 'bg-blue-500';
    return 'bg-blue-400/50';
  };
  return <>
      {/* No backdrop — table stays fully interactive while drawer is open.
                                          Closing happens via X, Esc, or selecting another quote. */}
      {/* Drawer */}
      <aside role="dialog" aria-label={`Quote ${quote.id} details`} className="fixed top-0 right-0 z-50 h-full w-full sm:w-[460px] bg-slate-900 border-l border-white/10 shadow-2xl flex flex-col" data-id="element-12198" data-mp="umv6r5kaewfx">
        {/* Header */}
        <header className="flex items-center gap-2 px-4 py-3 border-b border-white/10 flex-shrink-0" data-id="element-12199" data-mp="1zsbcmawr1tw">
          <div className="flex-1 min-w-0" data-id="element-12200" data-mp="6ylmi82q2gqw">
            <div className="flex items-center gap-2" data-id="element-12201" data-mp="6vh9cl7a4ao3">
              <span className="text-[11px] font-mono text-blue-400" data-id="element-12202" data-mp="bw50r7zf6tiu">
                {quote.id}
              </span>
              <StatusBadge status={quote.status} data-id="element-12203" data-mp="goy5hvuw21ux" />
            </div>
            <p className="text-sm text-white font-medium truncate mt-0.5" data-id="element-12204" data-mp="3fckhp7hvdy3c">
              {getQuoteName(quote)}
            </p>
          </div>
          {total > 1 && <div className="flex items-center gap-0.5 mr-1" data-id="element-12205" data-mp="3kadnhzf9gq33">
              <button onClick={onPrev} disabled={!hasPrev} aria-label="Previous quote" className="w-7 h-7 flex items-center justify-center rounded text-blue-200 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Previous (←)" data-id="element-12206" data-mp="12eaww6u7h2nx">
                <ChevronLeftIcon size={14} data-id="element-12207" data-mp="17bu80xvlwzbn" />
              </button>
              <span className="text-[10px] text-blue-300/60 tabular-nums px-1 min-w-[2.5rem] text-center" data-id="element-12208" data-mp="1lcevi7cgvjbh">
                {position + 1} / {total}
              </span>
              <button onClick={onNext} disabled={!hasNext} aria-label="Next quote" className="w-7 h-7 flex items-center justify-center rounded text-blue-200 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Next (→)" data-id="element-12209" data-mp="1h8yxp7onvcx7">
                <ChevronRightIcon size={14} data-id="element-12210" data-mp="2515o7q7pgpop" />
              </button>
            </div>}
          <button onClick={onClose} aria-label="Close" className="w-7 h-7 flex items-center justify-center rounded text-blue-200 hover:text-white hover:bg-white/10 transition-colors" title="Close (Esc)" data-id="element-12211" data-mp="202m5rzsblw13">
            <XIcon size={16} data-id="element-12212" data-mp="2nnn1cj67jz7b" />
          </button>
        </header>
        {/* Tabs */}
        <div className="flex items-center px-2 border-b border-white/10 flex-shrink-0 bg-slate-900" data-id="element-12213" data-mp="29o3sfo5kojty">
          {[{
          id: 'summary' as const,
          label: 'Summary',
          icon: <FileTextIcon size={12} data-id="element-12214" data-mp="1zytz64yzsv49" />
        }, {
          id: 'timeline' as const,
          label: 'Timeline',
          icon: <ClockIcon size={12} data-id="element-12215" data-mp="24r3uy1msgpeg" />
        }, {
          id: 'notes' as const,
          label: 'Notes',
          icon: <MessageSquareIcon size={12} data-id="element-12216" data-mp="1qzywh2v5q35p" />
        }].map(t => {
          const isActive = mode === t.id;
          return <button key={t.id} onClick={() => onModeChange(t.id as any)} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${isActive ? 'border-blue-400 text-white' : 'border-transparent text-blue-300/70 hover:text-white'}`} data-id="element-12217" data-mp="1vy0c1dtdjkcf">
                {t.icon}
                {t.label}
              </button>;
        })}
        </div>
        {/* Body */}
        <div className="flex-1 overflow-y-auto no-scrollbar" data-id="element-12218" data-mp="1murtksyyvlch">
          {mode === 'summary' && <section className="px-4 py-3" data-id="element-12219" data-mp="1rsjlhmzm37go">
              <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold mb-3" data-id="element-12220" data-mp="1rvpal288q9ce">
                Summary
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-12221" data-mp="20szo6u8j6akh">
                <div data-id="element-12222" data-mp="25t42cdlyut7j">
                  <p className="text-blue-300/60 mb-1" data-id="element-12223" data-mp="25sc9hajr79ky">Amount</p>
                  <p className="text-white font-semibold tabular-nums text-lg" data-id="element-12224" data-mp="25qrgvqh020pq">
                    ${quote.amount.toLocaleString()}
                  </p>
                </div>
                <div data-id="element-12225" data-mp="2fi44u6zog6qh">
                  <p className="text-blue-300/60 mb-1" data-id="element-12226" data-mp="2fkqgahpov6dz">Products</p>
                  <p className="text-white font-medium text-lg" data-id="element-12227" data-mp="2jm3kyoh1p3jb">
                    {productCount}{' '}
                    <span className="text-blue-300/70 text-base" data-id="element-12228" data-mp="2ojv8z7ekk5i8">
                      {PRODUCT_TYPE_LABEL[productType]}
                    </span>
                  </p>
                </div>
                <div data-id="element-12229" data-mp="7vlzy4fx5zk2">
                  <p className="text-blue-300/60 mb-1" data-id="element-12230" data-mp="7zi6cvhhlr9z">Dealer</p>
                  <p className="text-white" data-id="element-12231" data-mp="3wd5exd21xng">{quote.dealer || '—'}</p>
                </div>
                <div data-id="element-12232" data-mp="3v2wot0wyivxd">
                  <p className="text-blue-300/60 mb-1" data-id="element-12233" data-mp="3v3pmzdhuod3w">Customer</p>
                  <p className="text-white" data-id="element-12234" data-mp="vmztuetpy375">{quote.customer}</p>
                </div>
                <div data-id="element-12235" data-mp="rkmnbo75e19b">
                  <p className="text-blue-300/60 mb-1" data-id="element-12236" data-mp="rm7jtjd0vu9t">Quoted by</p>
                  <p className="text-white" data-id="element-12237" data-mp="mu657qsdtrgm">{getQuotedBy(quote)}</p>
                </div>
                <div data-id="element-12238" data-mp="hvcwebo1ioms">
                  <p className="text-blue-300/60 mb-1" data-id="element-12239" data-mp="hw4qtj5wigbh">Country</p>
                  <p className="text-white" data-id="element-12240" data-mp="htifr9jsxioi">{quote.country}</p>
                </div>
                <div data-id="element-12241" data-mp="1fctqb67dp6hp">
                  <p className="text-blue-300/60 mb-1" data-id="element-12242" data-mp="1ak0gw50yt9za">Created</p>
                  <p className="text-white tabular-nums" data-id="element-12243" data-mp="1aiphsiigsqei">
                    {formatLongDate(quote.date)}
                  </p>
                </div>
                <div data-id="element-12244" data-mp="1yfvhb9o030c9">
                  <p className="text-blue-300/60 mb-1" data-id="element-12245" data-mp="1ye0vlt4wn474">Expires</p>
                  <p className="text-white tabular-nums" data-id="element-12246" data-mp="1ko74f45xg7u8">
                    {formatLongDate(expiresAt)}
                  </p>
                </div>
                <div data-id="element-12247" data-mp="1onzjlzpttc8k">
                  <p className="text-blue-300/60 mb-1" data-id="element-12248" data-mp="1on68ynnqy2l5">Sale probability</p>
                  <span className={`inline-block text-sm font-semibold px-2 py-0.5 rounded border tabular-nums ${quote.probability > 70 ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : quote.probability >= 30 ? 'bg-amber-500/20 text-amber-300 border-amber-500/30' : 'bg-rose-500/20 text-rose-300 border-rose-500/30'}`} data-id="element-12249" data-mp="1ao6mnmnphzs5">
                    {quote.probability}%
                  </span>
                </div>
              </div>
            </section>}
          {mode === 'timeline' && <>
              {/* Closure note */}
              {closureNote && <section className="px-4 py-3 border-b border-white/5" data-id="element-12250" data-mp="ogufh7bb9vpd">
                  <div className="flex items-center gap-1.5 mb-2" data-id="element-12251" data-mp="oezrfk4qtnh2">
                    <FileTextIcon size={12} className={closureIcon} data-id="element-12252" data-mp="khbukbjhpkmj" />
                    <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12253" data-mp="y7f9ehg1ic1y">
                      {quote.status === 'Won' ? 'Win note' : 'Closure note'}
                    </p>
                  </div>
                  <div className={`rounded-md border px-3 py-2.5 ${closureBorder}`} data-id="element-12254" data-mp="183s1xbzumjv4">
                    <p className="text-sm text-white leading-relaxed" data-id="element-12255" data-mp="135gt94laifeh">
                      {closureNote.text}
                    </p>
                    <p className="text-[11px] text-blue-300/60 mt-2" data-id="element-12256" data-mp="1h3x8jjx7xe88">
                      — {closureNote.author}, {formatLongDate(closureNote.date)}
                    </p>
                  </div>
                </section>}
              {/* Activity timeline */}
              <section className="px-4 py-3" data-id="element-12257" data-mp="3dzlbjyzp1t4t">
                <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold mb-3" data-id="element-12258" data-mp="3e0ny5upzh9ex">
                  Activity timeline
                </p>
                <ol className="relative space-y-3" data-id="element-12259" data-mp="3kcyyq7f8ail5">
                  {timeline.map((ev, i) => {
                const isLast = i === timeline.length - 1;
                return <li key={`${ev.date}-${i}`} className="flex gap-3" data-id="element-12260" data-mp="3ud9e9yoqk23h" data-mpi={i}>
                        <div className="flex flex-col items-center flex-shrink-0" data-id="element-12261" data-mp="3uadc1y5uur98">
                          <span className={`w-2.5 h-2.5 rounded-full ${timelineDotColor(ev.kind, ev.toStatus)} ring-2 ring-slate-900`} data-id="element-12262" data-mp="2wmwak2efpwrt" />
                          {!isLast && <span className="w-px flex-1 bg-white/10 mt-1" data-id="element-12263" data-mp="36mvhjuv7hf2k" />}
                        </div>
                        <div className="flex-1 min-w-0 pb-1" data-id="element-12264" data-mp="3be59x3852wi5">
                          <div className="flex items-baseline justify-between gap-2" data-id="element-12265" data-mp="2dyhkpifusosl">
                            <p className="text-xs text-white font-medium" data-id="element-12266" data-mp="2dwn94uqh2916">
                              {ev.title}
                            </p>
                            <p className="text-[10px] text-blue-300/60 tabular-nums flex-shrink-0" data-id="element-12267" data-mp="2bd0czaxsnqkx">
                              {formatLongDate(ev.date)}
                            </p>
                          </div>
                          {ev.detail && <p className="text-[11px] text-blue-200/80 mt-1 leading-relaxed" data-id="element-12268" data-mp="21e135p1alowc">
                              {ev.detail}
                            </p>}
                          {ev.author && <p className="text-[10px] text-blue-300/50 mt-0.5" data-id="element-12269" data-mp="1sfz59icuvcda">
                              by {ev.author}
                            </p>}
                        </div>
                      </li>;
              })}
                </ol>
              </section>
            </>}
          {mode === 'notes' && <QuoteNotesPanel quote={quote} data-id="element-12270" data-mp="1drul09y5msse" />}
        </div>
        {/* Footer */}
        <footer className="px-4 py-3 border-t border-white/10 flex-shrink-0" data-id="element-12271" data-mp="18q578mjjs10m">
          <button type="button" className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 rounded-md transition-colors" data-id="element-12272" data-mp="14p36g19zfgwj">
            Open full quote
            <ChevronRightIcon size={14} data-id="element-12273" data-mp="z3ui49edd9vf" />
          </button>
        </footer>
      </aside>
    </>;
}
function AllQuotesExplorerView({
  role,
  configOpen,
  setConfigOpen
}: {
  role: string;
  configOpen: boolean;
  setConfigOpen: (open: boolean) => void;
}) {
  const dummyQuotes = useQuotes();
  const {
    name: userName
  } = useUser();
  const [chain, setChain] = useState<GroupDimension[]>(() => getDefaultChain(role));
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [chainPresetOpen, setChainPresetOpen] = useState(false);
  const [sort, setSort] = useState<{
    key: SortKey;
    dir: SortDir;
  }>({
    key: 'date',
    dir: 'desc'
  });
  const [sortOpen, setSortOpen] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  const [drawerMode, setDrawerMode] = useState<'summary' | 'timeline' | 'notes'>('summary');
  const handleQuoteAction = (id: string, action: 'view' | 'timeline' | 'notes') => {
    if (action === 'view') {
      // Placeholder — would route to the full quote page in production.
      // For prototype, open the drawer at timeline mode as a graceful fallback.
      setSelectedQuoteId(id);
      setDrawerMode('summary');
      return;
    }
    setSelectedQuoteId(id);
    setDrawerMode(action);
  };
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [filters, setFilters] = useState<QuoteFilters>(INITIAL_FILTERS);
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  // Available years from data (newest first)
  const availableYears = useMemo(() => {
    const ys = new Set<number>();
    dummyQuotes.forEach(q => {
      const y = new Date(q.date).getFullYear();
      if (!isNaN(y)) ys.add(y);
    });
    return Array.from(ys).sort((a, b) => b - a);
  }, [dummyQuotes]);
  // KPI preferences (persisted per role in localStorage)
  const kpiStorageKey = `quoteExplorer:kpiPrefs:${role}`;
  const [kpiPrefs, setKpiPrefs] = useState<KpiPrefs>(() => {
    if (typeof window === 'undefined') return getDefaultKpiPrefs(role);
    try {
      const raw = window.localStorage.getItem(kpiStorageKey);
      if (!raw) return getDefaultKpiPrefs(role);
      const parsed = JSON.parse(raw) as KpiPrefs;
      // Validate
      if (typeof parsed.showKpis === 'boolean' && Array.isArray(parsed.visibleKpis)) {
        const valid = parsed.visibleKpis.filter(k => ALL_KPIS.includes(k));
        // If the stored prefs predate a schema change and ALL ids are stale,
        // fall back to defaults so the user isn't left with an empty strip.
        if (valid.length === 0 && parsed.visibleKpis.length > 0) {
          return getDefaultKpiPrefs(role);
        }
        return {
          showKpis: parsed.showKpis,
          visibleKpis: valid
        };
      }
      return getDefaultKpiPrefs(role);
    } catch {
      return getDefaultKpiPrefs(role);
    }
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(kpiStorageKey, JSON.stringify(kpiPrefs));
    } catch {
      /* ignore quota errors */
    }
  }, [kpiPrefs, kpiStorageKey]);
  const toggleKpi = (id: KpiId) => setKpiPrefs(p => ({
    ...p,
    visibleKpis: p.visibleKpis.includes(id) ? p.visibleKpis.filter(k => k !== id) : [...p.visibleKpis, id]
  }));
  const resetKpiPrefs = () => setKpiPrefs(getDefaultKpiPrefs(role));
  const addKpi = (id: KpiId) => setKpiPrefs(p => p.visibleKpis.includes(id) ? p : {
    ...p,
    visibleKpis: [...p.visibleKpis, id]
  });
  // Drag-and-drop for KPI panel order
  const [kpiDragIndex, setKpiDragIndex] = useState<number | null>(null);
  const [kpiDropIndex, setKpiDropIndex] = useState<number | null>(null);
  const handleKpiDragStart = (i: number) => setKpiDragIndex(i);
  const handleKpiDragOver = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    if (kpiDragIndex === null || kpiDragIndex === i) return;
    setKpiDropIndex(i);
  };
  const handleKpiDrop = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    if (kpiDragIndex === null || kpiDragIndex === i) {
      setKpiDragIndex(null);
      setKpiDropIndex(null);
      return;
    }
    setKpiPrefs(p => {
      const next = [...p.visibleKpis];
      const [moved] = next.splice(kpiDragIndex, 1);
      next.splice(i, 0, moved);
      return {
        ...p,
        visibleKpis: next
      };
    });
    setKpiDragIndex(null);
    setKpiDropIndex(null);
  };
  const handleKpiDragEnd = () => {
    setKpiDragIndex(null);
    setKpiDropIndex(null);
  };
  // Drag-and-drop handlers for Group by chain
  const handleDragStart = (i: number) => setDragIndex(i);
  const handleDragOver = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) return;
    setDropIndex(i);
  };
  const handleDragLeave = () => {
    // keep dropIndex until drop or dragend to avoid flicker
  };
  const handleDrop = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) {
      setDragIndex(null);
      setDropIndex(null);
      return;
    }
    setChain(c => {
      const next = [...c];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(i, 0, moved);
      return next;
    });
    setDragIndex(null);
    setDropIndex(null);
  };
  const handleDragEnd = () => {
    setDragIndex(null);
    setDropIndex(null);
  };
  // Filter option lists (unique sorted values from dataset)
  const filterOptions = useMemo(() => {
    const uniqueSorted = (vals: string[]) => Array.from(new Set(vals)).sort((a, b) => a.localeCompare(b));
    return {
      status: ['Draft', 'Active', 'Expired', 'Won', 'Lost', 'Cancelled'] as QuoteStatus[],
      businessUnit: uniqueSorted(dummyQuotes.map(q => q.businessUnit)),
      salesManager: uniqueSorted(dummyQuotes.map(q => q.salesManager)),
      country: uniqueSorted(dummyQuotes.map(q => q.country)),
      dealer: uniqueSorted(dummyQuotes.map(q => q.dealer))
    };
  }, [dummyQuotes]);
  const activeFilterCount = filters.status.length + filters.businessUnit.length + filters.salesManager.length + filters.country.length + filters.dealer.length + filters.probabilityBucket.length + (filters.quotedByMe ? 1 : 0);
  const clearAllFilters = () => setFilters(EMPTY_FILTERS);
  // Filter quotes by date range, filters AND search
  const filteredQuotes = useMemo(() => {
    const probBucket = (p: number): 'high' | 'medium' | 'low' => p > 70 ? 'high' : p >= 30 ? 'medium' : 'low';
    let base = dummyQuotes.filter(q => {
      if (!quoteMatchesDateRange(q, dateRange)) return false;
      if (filters.quotedByMe && getQuotedBy(q) !== userName) return false;
      if (filters.status.length > 0 && !filters.status.includes(q.status)) return false;
      if (filters.businessUnit.length > 0 && !filters.businessUnit.includes(q.businessUnit)) return false;
      if (filters.salesManager.length > 0 && !filters.salesManager.includes(q.salesManager)) return false;
      if (filters.country.length > 0 && !filters.country.includes(q.country)) return false;
      if (filters.dealer.length > 0 && !filters.dealer.includes(q.dealer)) return false;
      if (filters.probabilityBucket.length > 0 && !filters.probabilityBucket.includes(probBucket(q.probability))) return false;
      return true;
    });
    const sQ = search.trim().toLowerCase();
    if (sQ) {
      base = base.filter(qu => qu.customer.toLowerCase().includes(sQ) || qu.dealer.toLowerCase().includes(sQ) || qu.salesManager.toLowerCase().includes(sQ) || qu.businessUnit.toLowerCase().includes(sQ) || qu.country.toLowerCase().includes(sQ) || qu.id.toLowerCase().includes(sQ));
    }
    return base;
  }, [search, filters, dateRange, userName]);
  // Sort applied to leaf quotes (groups stay sorted by openAmount desc)
  const sortedFilteredQuotes = useMemo(() => sortQuotes(filteredQuotes, sort), [filteredQuotes, sort]);
  // Build tree based on chain
  const tree = useMemo(() => buildTree(sortedFilteredQuotes, chain, 0, ''), [sortedFilteredQuotes, chain]);
  // Top-level aggregates
  const totals = useMemo(() => computeAggregates(filteredQuotes), [filteredQuotes]);
  // Statuses to display as columns in the tree (and in the KPI breakdown).
  // Mirrors the active status filter; falls back to Active+Expired when nothing is filtered.
  const statusesToShow: QuoteStatus[] = useMemo(() => filters.status.length > 0 ? filters.status : ['Active', 'Expired'], [filters.status]);
  const toggleExpanded = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);else next.add(key);
      return next;
    });
  };
  const expandAll = () => {
    const collect = (groups: TreeGroup[]): string[] => {
      const out: string[] = [];
      groups.forEach(g => {
        out.push(g.key);
        out.push(...collect(g.children));
      });
      return out;
    };
    setExpanded(new Set(collect(tree)));
  };
  const collapseAll = () => setExpanded(new Set());
  const available = (Object.keys(DIMENSION_META) as GroupDimension[]).filter(d => !chain.includes(d));
  const kpisNotVisible = ALL_KPIS.filter(id => !kpiPrefs.visibleKpis.includes(id));
  // Navigation between quotes in the drawer uses the current sorted/filtered list
  const navQuotes = sortedFilteredQuotes;
  const selectedIndex = selectedQuoteId ? navQuotes.findIndex(q => q.id === selectedQuoteId) : -1;
  const selectedQuote = selectedIndex >= 0 ? navQuotes[selectedIndex] : null;
  return <>
      {/* Quote detail drawer */}
      {selectedQuote && <QuoteDetailDrawer quote={selectedQuote} mode={drawerMode} onModeChange={setDrawerMode} onClose={() => setSelectedQuoteId(null)} onPrev={() => {
      if (selectedIndex > 0) setSelectedQuoteId(navQuotes[selectedIndex - 1].id);
    }} onNext={() => {
      if (selectedIndex >= 0 && selectedIndex < navQuotes.length - 1) setSelectedQuoteId(navQuotes[selectedIndex + 1].id);
    }} hasPrev={selectedIndex > 0} hasNext={selectedIndex >= 0 && selectedIndex < navQuotes.length - 1} position={selectedIndex} total={navQuotes.length} data-id="element-12274" data-mp="2vqherq3hxtcd" />}
      {/* Main content — single column, no flex sibling */}
      <div className="space-y-6" data-id="element-12275" data-mp="3bm5nlpfcaeol">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3" data-id="element-12276" data-mp="3bkkqpt5jyjnk">
          <div className="relative flex-1 max-w-md" data-id="element-12277" data-mp="37l3kuym7rkhv">
            <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-300/50" data-id="element-12278" data-mp="3ld9z2f5t3heo" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search quotes..." className="bg-white/5 border border-white/10 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full" data-id="element-12279" data-mp="3gddvgh10trzn" />
          </div>
          <div className="flex items-center gap-2" data-id="element-12280" data-mp="dtagzc1gz6az">
            {/* Export to Excel */}
            <button onClick={() => exportQuotesToCsv(sortedFilteredQuotes)} className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-blue-100 px-3 py-2 rounded-lg text-sm font-medium transition-colors" title={`Export ${sortedFilteredQuotes.length} quotes to Excel`} data-id="element-12281" data-mp="hu44w5ewwl3v">
              <DownloadIcon size={14} data-id="element-12282" data-mp="3q9e0cuoaqs2o" />
              <span className="hidden md:inline" data-id="element-12283" data-mp="3q8co4mvjnzjd">Export</span>
            </button>
            <button onClick={() => setConfigOpen(!configOpen)} aria-pressed={configOpen} aria-label="Toggle configuration panel" className={`relative flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${configOpen ? 'bg-blue-600 border-blue-500 text-white hover:bg-blue-700' : 'bg-white/5 border-white/10 text-blue-300 hover:bg-white/10 hover:text-white'}`} title="Toggle Configuration" data-id="element-12284" data-mp="3v1xfvx6jnxa4">
              <Settings2Icon size={16} data-id="element-12285" data-mp="3c9393qj2rbgz" />
              {activeFilterCount > 0 && <span className="absolute -top-1 -right-1 bg-[#4A8FD4] text-white text-[9px] font-bold px-1 min-w-[14px] h-[14px] rounded-full flex items-center justify-center" data-id="element-12286" data-mp="3rquwu5vhbgbe">
                  {activeFilterCount}
                </span>}
            </button>
            <button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-12287" data-mp="3ds3j4kkj3wns">
              <PlusIcon size={16} data-id="element-12288" data-mp="38uv1cl3alun2" />
              <span className="hidden sm:inline" data-id="element-12289" data-mp="341s7scyiml6v">New Quote</span>
            </button>
          </div>
        </div>

        {/* KPI strip (configurable per user) */}
        {kpiPrefs.showKpis && kpiPrefs.visibleKpis.length > 0 && <div className={`grid gap-4 ${kpiPrefs.visibleKpis.length === 1 ? 'grid-cols-1' : kpiPrefs.visibleKpis.length === 2 ? 'grid-cols-1 md:grid-cols-2' : kpiPrefs.visibleKpis.length === 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-4'}`} data-id="element-12290" data-mp="2v2wxopscoqya">
            {kpiPrefs.visibleKpis.map(id => {
          if (id === 'totalQuotes') {
            return <KPICard key={id} title="Total Quotes" value={filteredQuotes.length} icon={<ListIcon size={20} data-id="element-12292" data-mp="2fwr94nf4f89k" />} data-id="element-12291" data-mp="2fylsvyenouc2" />;
          }
          if (id === 'totalAmount') {
            return <KPICard key={id} title="Importe Total" value={`${totals.totalAmount.toLocaleString()}`} icon={<DollarSignIcon size={20} data-id="element-12294" data-mp="2pw08xwwo3h3u" />} data-id="element-12293" data-mp="2kyghfvayl7ty" />;
          }
          if (id === 'statusBreakdown') {
            const statusColors = STATUS_DOT_COLORS;
            const breakdown = statusesToShow.map(s => {
              const matching = filteredQuotes.filter(q => q.status === s);
              return {
                status: s,
                count: matching.length,
                amount: matching.reduce((acc, q) => acc + q.amount, 0)
              };
            });
            return <div key={id} className="bg-white/5 border border-white/10 rounded-xl p-4 backdrop-blur-sm flex flex-col" data-id="element-12295" data-mp="19hqlr645vyel">
                    <div className="flex items-center justify-between mb-3" data-id="element-12296" data-mp="1efiajr9if4ek">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12297" data-mp="mrqjime0wy70">
                        By Status
                      </p>
                      <TrendingUpIcon size={20} className="text-blue-400" data-id="element-12298" data-mp="ctj62diy0y9r" />
                    </div>
                    <div className="space-y-2 flex-1" data-id="element-12299" data-mp="hrkw5iesdxuq">
                      {breakdown.map(b => <div key={b.status} className="flex items-center justify-between gap-3" data-id="element-12300" data-mp="40pno7879iro">
                          <div className="flex items-center gap-2 min-w-0" data-id="element-12301" data-mp="8rfsnjcprjlu">
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusColors[b.status]}`} data-id="element-12302" data-mp="8tiqgpk3yh14" />
                            <span className="text-xs text-blue-100 font-medium truncate" data-id="element-12303" data-mp="3q633fhwwl9da">
                              {b.status}
                            </span>
                          </div>
                          <div className="flex items-baseline gap-2 flex-shrink-0" data-id="element-12304" data-mp="3h9lpk6x5mxdu">
                            <span className="text-sm font-bold text-white tabular-nums" data-id="element-12305" data-mp="3laxfkdjxxkmk">
                              {b.count}
                            </span>
                            <span className="text-[11px] text-blue-300/70 tabular-nums" data-id="element-12306" data-mp="2xmhbja6vx6nw">
                              ${b.amount.toLocaleString()}
                            </span>
                          </div>
                        </div>)}
                    </div>
                  </div>;
          }
          if (id === 'needsAttention') {
            return <KPICard key={id} title="Needs Attention" value={totals.needsAttention} icon={<AlertCircleIcon size={20} data-id="element-12308" data-mp="3gk1slv3er0xy" />} data-id="element-12307" data-mp="3lh20mbjtidrp" />;
          }
          return null;
        })}
          </div>}

        {/* Active filters strip */}
        {(activeFilterCount > 0 || !isDefaultDateRange(dateRange)) && <div className="flex items-center gap-2 flex-wrap bg-white/5 border border-amber-500/20 rounded-lg px-3 py-2 backdrop-blur-sm" data-id="element-12309" data-mp="3qc7vhzv772rx">
            <SlidersHorizontalIcon size={14} className="text-amber-400 flex-shrink-0" data-id="element-12310" data-mp="35xec3w235f1" />
            <span className="text-xs text-blue-200 font-medium flex-shrink-0" data-id="element-12311" data-mp="2wxnasppmuymp">
              {filteredQuotes.length} of {dummyQuotes.length} quotes
            </span>
            <span className="text-blue-300/30" data-id="element-12312" data-mp="30z8m46mir4us">·</span>
            {!isDefaultDateRange(dateRange) && <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-100 text-xs" data-id="element-12313" data-mp="35wie9u1alumb">
                <CalendarIcon size={11} className="text-amber-300" data-id="element-12314" data-mp="3foehtkm5ecxx" />
                <span className="font-medium" data-id="element-12315" data-mp="3fq7miud7an3a">
                  {formatDateRangeLabel(dateRange)}
                </span>
                <button onClick={() => setDateRange(DEFAULT_DATE_RANGE)} className="text-amber-300/70 hover:text-white rounded-full hover:bg-amber-500/20 w-4 h-4 flex items-center justify-center -mr-0.5" aria-label="Reset date range" data-id="element-12316" data-mp="3oot68sues85n">
                  <XIcon size={11} data-id="element-12317" data-mp="1ubtrt31rljzy" />
                </button>
              </div>}
            {filters.quotedByMe && <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-blue-500/15 border border-blue-400/40 text-blue-100 text-xs" data-id="element-12318" data-mp="1zaelskqfb7fy">
                <UserIcon size={11} className="text-blue-300" data-id="element-12319" data-mp="1mznjmqq1seh0" />
                <span className="font-medium" data-id="element-12320" data-mp="1my2pv3bk4zk7">Quoted by me</span>
                <button onClick={() => setFilters(f => ({
            ...f,
            quotedByMe: false
          }))} className="text-blue-300/70 hover:text-white rounded-full hover:bg-blue-500/20 w-4 h-4 flex items-center justify-center -mr-0.5" aria-label="Clear Quoted by me" data-id="element-12321" data-mp="1i1cayjbhoa0d">
                  <XIcon size={11} data-id="element-12322" data-mp="25pdfzi9gl8b1" />
                </button>
              </div>}
            {([{
          key: 'status' as const,
          label: 'Status',
          values: filters.status
        }, {
          key: 'businessUnit' as const,
          label: 'BU',
          values: filters.businessUnit
        }, {
          key: 'salesManager' as const,
          label: 'Sales Mgr',
          values: filters.salesManager
        }, {
          key: 'country' as const,
          label: 'Country',
          values: filters.country
        }, {
          key: 'dealer' as const,
          label: 'Dealer',
          values: filters.dealer
        }, {
          key: 'probabilityBucket' as const,
          label: 'Probability',
          values: filters.probabilityBucket.map(b => b === 'high' ? 'High' : b === 'medium' ? 'Medium' : 'Low')
        }] as const).filter(cat => cat.values.length > 0).map(cat => <div key={cat.key} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-100 text-xs" data-id="element-12323" data-mp="2rwxxsaxsipfi">
                  <span className="text-amber-300/70 font-medium" data-id="element-12324" data-mp="2iwjc3agwz8bh">
                    {cat.label}:
                  </span>
                  <span className="font-medium truncate max-w-[180px]" data-id="element-12325" data-mp="2mxl8zkncz8aa">
                    {cat.values.length <= 2 ? cat.values.join(', ') : `${cat.values.length} selected`}
                  </span>
                  <button onClick={() => setFilters(f => ({
            ...f,
            [cat.key]: []
          }))} className="text-amber-300/70 hover:text-white rounded-full hover:bg-amber-500/20 w-4 h-4 flex items-center justify-center -mr-0.5" aria-label={`Clear ${cat.label} filter`} data-id="element-12326" data-mp="1bj7sj0uxpv4a">
                    <XIcon size={11} data-id="element-12327" data-mp="1dy5vte9opsbf" />
                  </button>
                </div>)}
            <button onClick={clearAllFilters} className="ml-auto text-xs text-blue-300 hover:text-white underline underline-offset-2 decoration-blue-400/30 hover:decoration-white/60 px-2 py-0.5 rounded transition-colors flex-shrink-0" data-id="element-12328" data-mp="18yjhf1i4e611">
              Clear all
            </button>
          </div>}

        {/* Tree */}
        <div className="bg-[#213056] border border-white/10 rounded-xl overflow-hidden" data-id="element-12329" data-mp="uza5wf4v003e">
          {chain.length === 0 ? <div data-id="element-12330" data-mp="q70ib5r7vc4a">
              {filteredQuotes.length === 0 ? <div className="py-12 text-center text-sm text-blue-300/60" data-id="element-12331" data-mp="q6779sl860gc">
                  No quotes match your search.
                </div> : filteredQuotes.map(q => <QuoteLeafRow key={q.id} quote={q} depth={0} onOpen={id => {
            setSelectedQuoteId(id);
            setDrawerMode('timeline');
          }} onAction={handleQuoteAction} isSelected={q.id === selectedQuoteId} data-id="element-12332" data-mp="3tnpblq1sgpc0" />)}
            </div> : tree.length === 0 ? <div className="py-12 text-center text-sm text-blue-300/60" data-id="element-12333" data-mp="w03lvbi04s6j">
              No quotes match your search.
            </div> : <div data-id="element-12334" data-mp="10z6hy6g07o2d">
              {tree.map(node => <TreeRow key={node.key} node={node} depth={0} expanded={expanded} onToggle={toggleExpanded} isLast={false} statusesToShow={statusesToShow} onOpenQuote={id => {
            setSelectedQuoteId(id);
            setDrawerMode('timeline');
          }} onQuoteAction={handleQuoteAction} selectedQuoteId={selectedQuoteId} data-id="element-12335" data-mp="10y53rt3oylgf" />)}
            </div>}
        </div>
      </div>

      {/* Right config sidebar — page-level fixed positioning */}
      {configOpen ? <aside className="fixed top-[72px] bottom-0 right-0 z-30 w-72 bg-[#172554] border-l border-white/10 flex flex-col shadow-2xl" data-id="element-12336" data-mp="1op0v6p48jen5">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/20 flex-shrink-0" data-id="element-12337" data-mp="1tm0ywtdm45b3">
            <div className="flex items-center gap-2" data-id="element-12338" data-mp="3e7h6s03tqv7z">
              <Settings2Icon size={16} className="text-blue-400" data-id="element-12339" data-mp="397l2s0v4n3sf" />
              <h3 className="text-sm font-semibold text-white" data-id="element-12340" data-mp="34fjvktctg399">
                Configuration
              </h3>
              {activeFilterCount > 0 && <span className="bg-[#4A8FD4]/20 text-[#6AABEE] border border-[#4A8FD4]/40 text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-id="element-12341" data-mp="2zfyvgflwtouy">
                  {activeFilterCount}
                </span>}
            </div>
            <button onClick={() => setConfigOpen(false)} className="text-blue-300/70 hover:text-white p-1 rounded hover:bg-white/10 transition-colors" aria-label="Collapse configuration panel" data-id="element-12342" data-mp="2qeqxjynb2q1e">
              <PanelRightCloseIcon size={16} data-id="element-12343" data-mp="2gjp78zhanhx8" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-6 no-scrollbar" data-id="element-12344" data-mp="job0gpqgi6es">
            {/* Date range section */}
            <div data-id="element-12345" data-mp="erara5yb8njg">
              <div className="flex items-center justify-between mb-2" data-id="element-12346" data-mp="eoom2cyw6k4e">
                <div className="flex items-center gap-1.5" data-id="element-12347" data-mp="3jr2z5oln68cu">
                  <CalendarIcon size={12} className="text-[#6A85A8]" data-id="element-12348" data-mp="3oq5x6p37febb" />
                  <p className="text-xs text-[#6A85A8] font-semibold" data-id="element-12349" data-mp="3ay7rko8pc0jw">
                    Date range
                  </p>
                </div>
                {!isDefaultDateRange(dateRange) && <button onClick={() => setDateRange(DEFAULT_DATE_RANGE)} className="text-[10px] text-blue-300 hover:text-white px-1.5 py-0.5 rounded hover:bg-white/5 transition-colors" data-id="element-12350" data-mp="3exqbphsp87lo">
                    Reset
                  </button>}
              </div>
              <div className="border border-white/5 rounded-md bg-white/5 px-3 py-2 space-y-2" data-id="element-12351" data-mp="2s0f0qkbcrh2v">
                {/* Mode pills */}
                <div className="flex gap-1" data-id="element-12352" data-mp="2w18mp858ilt4">
                  {([{
                id: 'last12m',
                label: 'Last 12m'
              }, {
                id: 'year',
                label: 'Year'
              }, {
                id: 'month',
                label: 'Month'
              }] as {
                id: DateRangeMode;
                label: string;
              }[]).map(m => {
                const isOn = dateRange.mode === m.id;
                return <button key={m.id} onClick={() => setDateRange(prev => {
                  if (m.id === 'last12m') return {
                    mode: 'last12m',
                    year: new Date().getFullYear()
                  };
                  if (m.id === 'year') return {
                    mode: 'year',
                    year: prev.year ?? availableYears[0] ?? new Date().getFullYear()
                  };
                  return {
                    mode: 'month',
                    year: prev.year ?? availableYears[0] ?? new Date().getFullYear(),
                    month: prev.month ?? new Date().getMonth() + 1
                  };
                })} className={`flex-1 px-2 py-1 rounded-md text-xs font-semibold border transition-colors ${isOn ? 'bg-[#4A8FD4]/[0.18] text-[#9DCFEE] border-[#4A8FD4]/50' : 'bg-white/5 border-white/10 text-blue-300 hover:text-white hover:bg-white/10'}`} data-id="element-12353" data-mp="2oihix25a6don">
                        {m.label}
                      </button>;
              })}
                </div>
                {/* Year + Month selectors */}
                {dateRange.mode !== 'last12m' && <div className="flex gap-1" data-id="element-12354" data-mp="eyjkvtvzbrv7">
                    <select value={dateRange.year} onChange={e => setDateRange(prev => ({
                ...prev,
                year: Number(e.target.value)
              }))} className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-400" data-id="element-12355" data-mp="ezbitbd1u1on">
                      {availableYears.length === 0 ? [<option key={new Date().getFullYear()} value={new Date().getFullYear()} data-id="element-12356" data-mp="xvb4k4ffo8zs">
                              {new Date().getFullYear()}
                            </option>] : availableYears.map(y => <option key={y} value={y} data-id="element-12357" data-mp="1qdp1m1sy3qxt">
                              {y}
                            </option>)}
                    </select>
                    {dateRange.mode === 'month' && <select value={dateRange.month ?? 1} onChange={e => setDateRange(prev => ({
                ...prev,
                month: Number(e.target.value)
              }))} className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-400" data-id="element-12358" data-mp="364hkz9o5lmg4">
                        {MONTH_NAMES.map((m, idx) => <option key={m} value={idx + 1} data-id="element-12359" data-mp="2s6awul90b0tn" data-mpi={idx}>
                            {m}
                          </option>)}
                      </select>}
                  </div>}
                {/* Active range label */}
                <p className="text-[10px] text-blue-300/60 pt-0.5" data-id="element-12360" data-mp="2x3b3oydok7lt">
                  Showing: {formatDateRangeLabel(dateRange)}
                </p>
              </div>
            </div>
            {/* Filters section */}
            <div data-id="element-12361" data-mp="cdtzs7pjhh1o">
              <div className="flex items-center justify-between mb-2" data-id="element-12362" data-mp="ccaiqew271yu">
                <div className="flex items-center gap-1.5" data-id="element-12363" data-mp="3582cnitqkqn6">
                  <SlidersHorizontalIcon size={12} className="text-[#6A85A8]" data-id="element-12364" data-mp="359n592whpzie" />
                  <p className="text-xs text-[#6A85A8] font-semibold" data-id="element-12365" data-mp="3a52hxrirhpd8">
                    Filters
                  </p>
                  {activeFilterCount > 0 && <span className="bg-[#4A8FD4]/20 text-[#6AABEE] border border-[#4A8FD4]/40 text-[10px] font-bold px-1.5 py-0 rounded-full" data-id="element-12366" data-mp="3j71ykipv6rfn">
                      {activeFilterCount}
                    </span>}
                </div>
                {activeFilterCount > 0 && <button onClick={clearAllFilters} className="text-[10px] text-blue-300 hover:text-white px-1.5 py-0.5 rounded hover:bg-white/5 transition-colors" data-id="element-12367" data-mp="2rgdyzb3u9u43">
                    Clear all
                  </button>}
              </div>
              <div className="space-y-2" data-id="element-12368" data-mp="23qtdolk7qkrn">
                {/* Status — inline toggle pills */}
                <div className="border border-white/5 rounded-md bg-white/5 px-3 py-2" data-id="element-12369" data-mp="27rwv3chwsouz">
                  <p className="text-xs text-[#6A85A8] font-semibold mb-1.5" data-id="element-12370" data-mp="208nkjfxo2k4c">
                    Status
                  </p>
                  <div className="flex flex-wrap gap-1" data-id="element-12371" data-mp="1v9azn0qcvn4e">
                    {filterOptions.status.map(s => {
                  const isOn = filters.status.includes(s);
                  return <button key={s} onClick={() => setFilters(f => ({
                    ...f,
                    status: isOn ? f.status.filter(x => x !== s) : [...f.status, s]
                  }))} className={`px-2 py-0.5 rounded-md text-xs font-semibold border transition-colors ${isOn ? 'bg-[#4A8FD4]/[0.18] text-[#9DCFEE] border-[#4A8FD4]/50' : 'bg-white/5 border-white/10 text-blue-300 hover:text-white hover:bg-white/10'}`} data-id="element-12372" data-mp="1m8uyhxx34s2p">
                          {s}
                        </button>;
                })}
                  </div>
                </div>
                {/* Group by — moved inline here, between Status and entity filters */}
                <div className="border border-white/5 rounded-md bg-white/5 px-3 py-2" data-id="element-12373" data-mp="tlie9ueecwck">
                  <div className="flex items-center justify-between mb-2" data-id="element-12374" data-mp="2tnhy9ugaerpt">
                    <p className="text-xs text-[#6A85A8] font-semibold" data-id="element-12375" data-mp="2ym1hhn3hxpeh">
                      Group by
                    </p>
                    <span className="text-[10px] text-blue-300/50" data-id="element-12376" data-mp="2jvuaq4muputm">
                      Drag to reorder
                    </span>
                  </div>
                  {chain.length === 0 ? <p className="text-xs text-blue-300/60 italic py-2 px-2 bg-white/5 rounded-md border border-dashed border-white/10" data-id="element-12377" data-mp="2avpcajzmy6rl">
                      No grouping — showing flat list
                    </p> : <div className="space-y-1" data-id="element-12378" data-mp="20ykpqj1vo9a9">
                      {chain.map((d, i) => <Fragment key={d} data-id="element-12379" data-mp="25wulaurilbwe" data-mpi={i}>
                          <GroupChainChip dimension={d} canRemove={true} onRemove={() => setChain(c => c.filter(x => x !== d))} index={i} isDragging={dragIndex === i} isDropTarget={dropIndex === i && dragIndex !== i} onDragStart={handleDragStart} onDragOver={handleDragOver} onDrop={handleDrop} onDragEnd={handleDragEnd} onDragLeave={handleDragLeave} data-id="element-12380" data-mp="25xxdr74hjmef" />
                          {i < chain.length - 1 && <div className="flex justify-center py-0.5" data-id="element-12381" data-mp="1joyvewhnafej">
                              <ChevronDownIcon size={12} className="text-blue-300/40" data-id="element-12382" data-mp="1ydcwp2zexvsz" />
                            </div>}
                        </Fragment>)}
                    </div>}
                  <div className="mt-2" data-id="element-12383" data-mp="1tkjt4ifoktih">
                    <AddLevelDropdown available={available} onAdd={d => setChain(c => [...c, d])} data-id="element-12384" data-mp="27di43d4lwu75" />
                  </div>
                </div>
                {/* Multi-select accordions */}
                <FilterAccordionSection label="Business unit" options={filterOptions.businessUnit} selected={filters.businessUnit} onChange={next => setFilters(f => ({
              ...f,
              businessUnit: next
            }))} data-id="element-12385" data-mp="2hbxl2n9x6n2x" />
                <FilterAccordionSection label="Sales manager" options={filterOptions.salesManager} selected={filters.salesManager} onChange={next => setFilters(f => ({
              ...f,
              salesManager: next
            }))} data-id="element-12386" data-mp="myzgmkawikni" />
                <FilterAccordionSection label="Country" options={filterOptions.country} selected={filters.country} onChange={next => setFilters(f => ({
              ...f,
              country: next
            }))} data-id="element-12387" data-mp="17plcg2dkow1y" />
                <FilterAccordionSection label="Dealer" options={filterOptions.dealer} selected={filters.dealer} onChange={next => setFilters(f => ({
              ...f,
              dealer: next
            }))} data-id="element-12388" data-mp="1lmqfdthzpesz" />
                {/* Probability — inline toggle pills (moved to after Dealer) */}
                <div className="border border-white/5 rounded-md bg-white/5 px-3 py-2" data-id="element-12389" data-mp="1zeomjys550mi">
                  <p className="text-xs text-[#6A85A8] font-semibold mb-1.5" data-id="element-12390" data-mp="24dhwxic4sbig">
                    Probability
                  </p>
                  <div className="flex flex-wrap gap-1" data-id="element-12391" data-mp="29dnjik3jn7to">
                    {([{
                  k: 'high',
                  label: 'High >70%',
                  color: 'emerald'
                }, {
                  k: 'medium',
                  label: 'Medium 30–70%',
                  color: 'amber'
                }, {
                  k: 'low',
                  label: 'Low <30%',
                  color: 'red'
                }] as const).map(b => {
                  const isOn = filters.probabilityBucket.includes(b.k);
                  return <button key={b.k} onClick={() => setFilters(f => ({
                    ...f,
                    probabilityBucket: isOn ? f.probabilityBucket.filter(x => x !== b.k) : [...f.probabilityBucket, b.k]
                  }))} className={`px-2 py-0.5 rounded-md text-xs font-semibold border transition-colors ${isOn ? 'bg-[#4A8FD4]/[0.18] text-[#9DCFEE] border-[#4A8FD4]/50' : 'bg-white/5 border-white/10 text-blue-300 hover:text-white hover:bg-white/10'}`} data-id="element-12392" data-mp="3jyk0ni3v4bml">
                          {b.label}
                        </button>;
                })}
                  </div>
                </div>
              </div>
            </div>

            {/* Presets section */}
            <div data-id="element-12393" data-mp="7pgmz620v5cj">
              <p className="text-xs text-[#6A85A8] font-semibold mb-2" data-id="element-12394" data-mp="7oxa43r8b75d">
                Presets
              </p>
              <div className="space-y-1" data-id="element-12395" data-mp="3uvpujgbgaizo">
                {/* Filter preset: Quoted by me */}
                <button onClick={() => setFilters(f => ({
              ...f,
              quotedByMe: !f.quotedByMe
            }))} aria-pressed={filters.quotedByMe} className={`w-full flex items-start gap-2 text-left px-3 py-2 rounded-md border transition-colors ${filters.quotedByMe ? 'bg-blue-500/25 border-blue-400/50' : 'bg-white/5 border-white/5 hover:bg-white/10'}`} data-id="element-12396" data-mp="3pfzyx0uxltv">
                  <UserIcon size={12} className={`mt-0.5 flex-shrink-0 ${filters.quotedByMe ? 'text-blue-200' : 'text-blue-400/70'}`} data-id="element-12397" data-mp="hixjihtjmyjw" />
                  <div className="flex-1 min-w-0" data-id="element-12398" data-mp="327bsmph8r2iu">
                    <div className="flex items-center justify-between gap-2" data-id="element-12399" data-mp="325qyv22r3nm1">
                      <span className={`text-xs font-medium ${filters.quotedByMe ? 'text-white' : 'text-blue-100'}`} data-id="element-12400" data-mp="376y7128t2ot8">
                        Quoted by me
                      </span>
                      {filters.quotedByMe && <CheckCircleIcon size={12} className="text-blue-300" data-id="element-12401" data-mp="2umrjr8mboi8h" />}
                    </div>
                    <p className="text-[10px] text-blue-300/60 mt-0.5" data-id="element-12402" data-mp="38mshwgb7dbk9">
                      Only quotes you created
                    </p>
                  </div>
                </button>
                {/* Chain presets dropdown */}
                {(() => {
              const chainPresets: {
                label: string;
                description: string;
                chain: GroupDimension[];
              }[] = [{
                label: `Default for ${getRoleLabel(role)}`,
                description: 'The grouping the system suggests for this role to manage quotes',
                chain: getDefaultChain(role)
              }, {
                label: 'By Sales Manager',
                description: 'Group quotes by sales manager, then dealer',
                chain: ['salesManager', 'dealer']
              }, {
                label: 'By Dealer',
                description: 'Flat list grouped by dealer',
                chain: ['dealer']
              }, {
                label: 'By Probability',
                description: 'Group by High / Medium / Low close-rate buckets',
                chain: ['probability']
              }, {
                label: 'Flat list',
                description: 'No grouping — show every quote in a row',
                chain: []
              }];
              const chainKey = chain.join('>');
              const activePreset = chainPresets.find(p => p.chain.join('>') === chainKey);
              const triggerLabel = activePreset?.label ?? 'Custom grouping';
              const triggerDescription = activePreset?.description ?? 'Your own grouping configuration';
              const otherPresets = activePreset ? chainPresets.filter(p => p.chain.join('>') !== chainKey) : chainPresets;
              return <div className="relative" data-id="element-12403" data-mp="1vgupwv500ypx">
                      <button onClick={() => setChainPresetOpen(o => !o)} aria-haspopup="listbox" aria-expanded={chainPresetOpen} className="w-full flex items-center justify-between gap-2 text-left px-3 py-2 rounded-md bg-white/5 border border-white/5 hover:bg-white/10 transition-colors" data-id="element-12404" data-mp="1vi5swsqme2fz">
                        <div className="flex-1 min-w-0" data-id="element-12405" data-mp="2990wbpicnrc6">
                          <p className="text-xs font-medium text-white truncate" data-id="element-12406" data-mp="2e7c9oa7usjz5">
                            {triggerLabel}
                          </p>
                          <p className="text-[10px] text-blue-300/60 mt-0.5 truncate" data-id="element-12407" data-mp="2j805raaxvr08">
                            {triggerDescription}
                          </p>
                        </div>
                        <ChevronDownIcon size={14} className={`text-blue-300/70 flex-shrink-0 transition-transform ${chainPresetOpen ? 'rotate-180' : ''}`} data-id="element-12408" data-mp="2s4hkeniii70q" />
                      </button>
                      {chainPresetOpen && <>
                          <div className="fixed inset-0 z-10" onClick={() => setChainPresetOpen(false)} data-id="element-12409" data-mp="2138d32t3qpu5" />
                          <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-slate-900 border border-white/10 rounded-md shadow-lg overflow-hidden" data-id="element-12410" data-mp="1w4wxebg4ll40">
                            {otherPresets.length === 0 ? <div className="px-3 py-2 text-[11px] text-blue-300/50 italic" data-id="element-12411" data-mp="262t7r118oc12">
                                No other options
                              </div> : otherPresets.map(p => <button key={p.label} type="button" onClick={() => {
                      setChain(p.chain);
                      setChainPresetOpen(false);
                    }} className="w-full text-left px-3 py-2 hover:bg-white/10 transition-colors" data-id="element-12412" data-mp="2jzzpcsa884op">
                                  <p className="text-xs text-blue-100 font-medium" data-id="element-12413" data-mp="2otkq2s37fcrl">
                                    {p.label}
                                  </p>
                                  <p className="text-[10px] text-blue-300/60 mt-0.5" data-id="element-12414" data-mp="2xuimopbbci05">
                                    {p.description}
                                  </p>
                                </button>)}
                          </div>
                        </>}
                    </div>;
            })()}
              </div>
            </div>

            {/* Sort section */}
            <div data-id="element-12415" data-mp="3cg678blfhojj">
              <div className="flex items-center gap-1.5 mb-2" data-id="element-12416" data-mp="3cfocxkibtke9">
                <ArrowUpDownIcon size={12} className="text-[#6A85A8]" data-id="element-12417" data-mp="2yp1dfq4qrqh4" />
                <p className="text-xs text-[#6A85A8] font-semibold" data-id="element-12418" data-mp="2ynqbzwyrqut6">Sort by</p>
              </div>
              <div className="border border-white/5 rounded-md bg-white/5 px-3 py-2 space-y-2" data-id="element-12419" data-mp="33h2nr6icn74i">
                <div className="flex gap-1" data-id="element-12420" data-mp="2osomv14htsql">
                  {/* Custom dropdown trigger (replaces native select for proper dark theme) */}
                  <div className="relative flex-1 min-w-0" data-id="element-12421" data-mp="2fqfeohwp6bgd">
                    <button onClick={() => setSortOpen(o => !o)} aria-haspopup="listbox" aria-expanded={sortOpen} className="w-full flex items-center justify-between gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded px-2 py-1 text-xs text-white transition-colors" data-id="element-12422" data-mp="2fqysbmfbedol">
                      <span className="truncate" data-id="element-12423" data-mp="27q0zjx5wp2nb">
                        {SORT_OPTIONS.find(o => o.key === sort.key)?.label}
                      </span>
                      <ChevronDownIcon size={12} className={`text-blue-300/70 flex-shrink-0 transition-transform ${sortOpen ? 'rotate-180' : ''}`} data-id="element-12424" data-mp="1yxffmdqjgpww" />
                    </button>
                    {sortOpen && <>
                        <div className="fixed inset-0 z-10" onClick={() => setSortOpen(false)} data-id="element-12425" data-mp="1oyq91h43ervy" />
                        <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-slate-900 border border-white/10 rounded-md shadow-lg overflow-hidden max-h-72 overflow-y-auto" data-id="element-12426" data-mp="1jz50cez4f561">
                          {SORT_OPTIONS.map(o => {
                      const isActive = sort.key === o.key;
                      return <button key={o.key} type="button" onClick={() => {
                        setSort(s => ({
                          ...s,
                          key: o.key
                        }));
                        setSortOpen(false);
                      }} className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-center justify-between ${isActive ? 'bg-blue-500/15 text-white' : 'text-blue-100 hover:bg-white/10'}`} data-id="element-12427" data-mp="3ac2th66c7cim">
                                <span data-id="element-12428" data-mp="d5pbg4d8gi8z">{o.label}</span>
                                {isActive && <CheckCircleIcon size={12} className="text-blue-300" data-id="element-12429" data-mp="d7a9i3yrajbl" />}
                              </button>;
                    })}
                        </div>
                      </>}
                  </div>
                  <button onClick={() => setSort(s => ({
                ...s,
                dir: s.dir === 'asc' ? 'desc' : 'asc'
              }))} className="flex-shrink-0 inline-flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded px-2 py-1 text-[11px] text-blue-100 hover:text-white transition-colors" title={`Switch to ${sort.dir === 'asc' ? 'descending' : 'ascending'}`} data-id="element-12430" data-mp="m8qic79xaddp">
                    {sort.dir === 'asc' ? <>
                        <ArrowUpIcon size={11} data-id="element-12431" data-mp="3ulf12ouvqxs2" />
                        Asc
                      </> : <>
                        <ArrowDownIcon size={11} data-id="element-12432" data-mp="cg20x250hof3" />
                        Desc
                      </>}
                  </button>
                </div>
                <p className="text-[10px] text-blue-300/60" data-id="element-12433" data-mp="3fx0ymf1dlbc1">
                  Applies to quote rows inside each group.
                </p>
              </div>
            </div>
            {/* View section */}
            <div data-id="element-12434" data-mp="3pnlwg0986btw">
              <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold mb-2" data-id="element-12435" data-mp="3po5cfbfbeq5a">
                View
              </p>
              {/* Tree controls */}
              <div className="grid grid-cols-2 gap-1 mb-3" data-id="element-12436" data-mp="2x69gop3bocm1">
                <button onClick={expandAll} className="text-xs text-blue-100 hover:text-white px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/5 transition-colors" data-id="element-12437" data-mp="2x4q0eyho21k9">
                  Expand all
                </button>
                <button onClick={collapseAll} className="text-xs text-blue-100 hover:text-white px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/5 transition-colors" data-id="element-12438" data-mp="1cgxfnt0jzrh6">
                  Collapse all
                </button>
              </div>
              {/* KPI panels customization */}
              <div className="border border-white/5 rounded-md bg-white/5 overflow-hidden" data-id="element-12439" data-mp="1qfwz8m97lg6h">
                <div className="flex items-center justify-between px-3 py-2 border-b border-white/5" data-id="element-12440" data-mp="1vfi0x4frk2mw">
                  <span className="text-xs font-medium text-white" data-id="element-12441" data-mp="1hg9ym2ob0eeh">
                    KPI panels
                  </span>
                  <button onClick={() => setKpiPrefs(p => ({
                ...p,
                showKpis: !p.showKpis
              }))} role="switch" aria-checked={kpiPrefs.showKpis} aria-label="Toggle KPI panels visibility" className={`relative w-9 h-5 rounded-full transition-colors ${kpiPrefs.showKpis ? 'bg-blue-500' : 'bg-white/10'}`} data-id="element-12442" data-mp="1md0dwnsab5yu">
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${kpiPrefs.showKpis ? 'translate-x-[18px]' : 'translate-x-0.5'}`} data-id="element-12443" data-mp="xnwjv48yqd1q" />
                  </button>
                </div>
                {kpiPrefs.showKpis && <div className="px-2 py-2 flex flex-col gap-1" data-id="element-12444" data-mp="nr0h7f5qwl53">
                    {kpiPrefs.visibleKpis.length === 0 ? <p className="text-xs text-blue-300/50 italic py-1 px-1" data-id="element-12445" data-mp="1lc607qneq8lh">
                        No panels — add one below
                      </p> : kpiPrefs.visibleKpis.map((id, idx) => {
                const isDragging = kpiDragIndex === idx;
                const isDropTarget = kpiDropIndex === idx && kpiDragIndex !== idx;
                return <div key={id} draggable onDragStart={() => handleKpiDragStart(idx)} onDragOver={e => handleKpiDragOver(e, idx)} onDrop={e => handleKpiDrop(e, idx)} onDragEnd={handleKpiDragEnd} className={`flex items-center gap-2 rounded px-2 py-1.5 border select-none cursor-grab active:cursor-grabbing transition-all ${isDragging ? 'opacity-40 bg-blue-500/30 border-blue-400/60' : isDropTarget ? 'bg-blue-500/30 border-blue-300 ring-2 ring-blue-400/40' : 'bg-white/5 border-white/10 hover:bg-white/10'}`} data-id="element-12446" data-mp="16mqit19rnfig" data-mpi={idx}>
                            <GripVerticalIcon size={14} className="text-blue-300/60 flex-shrink-0" aria-hidden data-id="element-12447" data-mp="2lle3ptoysum2" />
                            <span className="text-xs text-blue-100 flex-1 truncate" data-id="element-12448" data-mp="2po0xw193yrmf">
                              {KPI_META[id].label}
                            </span>
                            <button onClick={e => {
                    e.stopPropagation();
                    toggleKpi(id);
                  }} onMouseDown={e => e.stopPropagation()} aria-label={`Remove ${KPI_META[id].label}`} className="text-blue-300/70 hover:text-white rounded-full hover:bg-blue-500/20 w-4 h-4 flex items-center justify-center flex-shrink-0" type="button" data-id="element-12449" data-mp="2zd2bjks6kwx8">
                              ×
                            </button>
                          </div>;
              })}
                    <div className="pt-1" data-id="element-12450" data-mp="gpw0xd2nfmvt">
                      <AddKpiDropdown available={kpisNotVisible} addKpi={addKpi} data-id="element-12451" data-mp="lnp92bniiqyq" />
                    </div>
                  </div>}
                <button onClick={resetKpiPrefs} className="w-full text-[10px] uppercase tracking-wider text-blue-300 hover:text-white px-3 py-1.5 border-t border-white/5 hover:bg-white/5 transition-colors" data-id="element-12452" data-mp="qkfp4yzbhmk5">
                  Reset to default
                </button>
              </div>
            </div>
          </div>
        </aside> : null}
    </>;
}
function AddKpiDropdown({
  available,
  addKpi
}: {
  available: KpiId[];
  addKpi: (id: KpiId) => void;
}) {
  const [open, setOpen] = useState(false);
  if (available.length === 0) {
    // All panels added, render disabled button
    return <button disabled className="w-full px-2 py-1.5 text-xs text-blue-300 bg-white/10 rounded border border-white/20 cursor-not-allowed" data-id="element-12453" data-mp="1iehs9i5qsz4q">
        All panels added
      </button>;
  }
  return <div className="relative" data-id="element-12454" data-mp="37uts5p16yceq">
      <button onClick={() => setOpen(!open)} className="w-full px-2 py-1.5 text-xs text-blue-300 bg-white/10 rounded border border-white/20 hover:bg-white/20 transition-colors flex items-center justify-center gap-1" type="button" data-id="element-12455" data-mp="37rxu80pcfnqa">
        <PlusIcon size={12} data-id="element-12456" data-mp="2tuj2gpbmb3v5" />
        Add panel
      </button>
      {open && <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} data-id="element-12457" data-mp="3qkii13q6r2n8" />
          <div className="absolute left-0 top-full mt-1 z-20 w-40 bg-slate-900 border border-white/10 rounded shadow-lg" data-id="element-12458" data-mp="3qmmjlf0m3pkc">
            {available.map(id => <button key={id} onClick={() => {
          addKpi(id);
          setOpen(false);
        }} className="block w-full text-left px-3 py-2 text-sm text-blue-100 hover:bg-white/10 transition-colors" type="button" data-id="element-12459" data-mp="3hrorub97aiul">
                {KPI_META[id].label}
              </button>)}
          </div>
        </>}
    </div>;
}
// --- Business Units View (CEO cascading drilldown) ---
function BusinessUnitsView() {
  const dummyQuotes = useQuotes();
  const [selectedBU, setSelectedBU] = useState<string | null>(null);
  const [selectedSM, setSelectedSM] = useState<string | null>(null);
  const [expandedDealer, setExpandedDealer] = useState<string | null>(null);
  const closedStatuses: QuoteStatus[] = ['Won', 'Lost', 'Expired', 'Cancelled'];
  // Level 1: BUs aggregated
  const buAggregates = useMemo(() => {
    const map = new Map<string, {
      name: string;
      salesManagers: Set<string>;
      dealers: Set<string>;
      countries: Set<string>;
      totalQuotes: number;
      openCount: number;
      wonCount: number;
      lostCount: number;
      openPipeline: number;
      weightedPipeline: number;
      wonAmount: number;
      needsAttention: number;
    }>();
    dummyQuotes.forEach(q => {
      const e = map.get(q.businessUnit) || {
        name: q.businessUnit,
        salesManagers: new Set<string>(),
        dealers: new Set<string>(),
        countries: new Set<string>(),
        totalQuotes: 0,
        openCount: 0,
        wonCount: 0,
        lostCount: 0,
        openPipeline: 0,
        weightedPipeline: 0,
        wonAmount: 0,
        needsAttention: 0
      };
      e.salesManagers.add(q.salesManager);
      e.dealers.add(q.dealer);
      e.countries.add(q.country);
      e.totalQuotes += 1;
      if (!closedStatuses.includes(q.status)) {
        e.openCount += 1;
        e.openPipeline += q.amount;
        e.weightedPipeline += q.amount * q.probability / 100;
      }
      if (q.status === 'Won') {
        e.wonCount += 1;
        e.wonAmount += q.amount;
      }
      if (q.status === 'Lost' || q.status === 'Expired') e.lostCount += 1;
      if (q.needsAttention) e.needsAttention += 1;
      map.set(q.businessUnit, e);
    });
    return Array.from(map.values()).map(b => {
      const decided = b.wonCount + b.lostCount;
      return {
        ...b,
        winRate: decided > 0 ? Math.round(b.wonCount / decided * 100) : 0
      };
    }).sort((a, b) => b.openPipeline - a.openPipeline);
  }, [dummyQuotes]);
  // Level 2: Sales Managers within selected BU
  const smAggregates = useMemo(() => {
    if (!selectedBU) return [];
    const map = new Map<string, {
      name: string;
      dealers: Set<string>;
      totalQuotes: number;
      openCount: number;
      wonCount: number;
      lostCount: number;
      openPipeline: number;
      weightedPipeline: number;
      wonAmount: number;
      needsAttention: number;
      lastActivity: string;
    }>();
    dummyQuotes.filter(q => q.businessUnit === selectedBU).forEach(q => {
      const e = map.get(q.salesManager) || {
        name: q.salesManager,
        dealers: new Set<string>(),
        totalQuotes: 0,
        openCount: 0,
        wonCount: 0,
        lostCount: 0,
        openPipeline: 0,
        weightedPipeline: 0,
        wonAmount: 0,
        needsAttention: 0,
        lastActivity: q.lastUpdated
      };
      e.dealers.add(q.dealer);
      e.totalQuotes += 1;
      if (!closedStatuses.includes(q.status)) {
        e.openCount += 1;
        e.openPipeline += q.amount;
        e.weightedPipeline += q.amount * q.probability / 100;
      }
      if (q.status === 'Won') {
        e.wonCount += 1;
        e.wonAmount += q.amount;
      }
      if (q.status === 'Lost' || q.status === 'Expired') e.lostCount += 1;
      if (q.needsAttention) e.needsAttention += 1;
      if (q.lastUpdated > e.lastActivity) e.lastActivity = q.lastUpdated;
      map.set(q.salesManager, e);
    });
    return Array.from(map.values()).map(s => {
      const decided = s.wonCount + s.lostCount;
      return {
        ...s,
        winRate: decided > 0 ? Math.round(s.wonCount / decided * 100) : 0
      };
    }).sort((a, b) => b.openPipeline - a.openPipeline);
  }, [selectedBU, dummyQuotes]);
  // Level 3: Dealers managed by selected Sales Manager
  const dealerAggregates = useMemo(() => {
    if (!selectedBU || !selectedSM) return [];
    const dealerQuotes = dummyQuotes.filter(q => q.businessUnit === selectedBU && q.salesManager === selectedSM);
    return aggregateByDealer(dealerQuotes);
  }, [selectedBU, selectedSM, dummyQuotes]);
  const getInitials = (name: string) => name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();
  return <div className="space-y-6" data-id="element-12460" data-mp="3l0onzreetmst">
      <div className="flex items-start justify-between flex-wrap gap-3" data-id="element-12461" data-mp="3l1pxhrfs218f">
        <div data-id="element-12462" data-mp="cl4xh054iihg">
          <h2 className="text-xl font-bold text-white" data-id="element-12463" data-mp="ckmqp9ku4lva">By Business Unit</h2>
          <p className="text-sm text-blue-300/70 mt-0.5" data-id="element-12464" data-mp="gmgmb3g6azol">
            Drill down from Business Unit → Sales Manager → Dealer → Quote
          </p>
          <div className="flex items-center gap-1.5 mt-2 text-sm" data-id="element-12465" data-mp="2uaa1sg55cuf">
            <button onClick={() => {
            setSelectedBU(null);
            setSelectedSM(null);
            setExpandedDealer(null);
          }} className={`flex items-center gap-1.5 px-2 py-1 rounded transition-colors ${selectedBU ? 'text-blue-300 hover:bg-white/10' : 'text-white font-semibold bg-white/5'}`} data-id="element-12466" data-mp="7tmt023w5zrq">
              <NetworkIcon size={14} data-id="element-12467" data-mp="19o7s2qte9xv6" />
              All Business Units
            </button>
            {selectedBU && <>
                <ChevronRightIcon size={13} className="text-blue-300/40" data-id="element-12468" data-mp="15nfhnu8sdmxb" />
                <button onClick={() => {
              setSelectedSM(null);
              setExpandedDealer(null);
            }} className={`flex items-center gap-1.5 px-2 py-1 rounded transition-colors ${selectedSM ? 'text-blue-300 hover:bg-white/10' : 'text-white font-semibold bg-white/5'}`} data-id="element-12469" data-mp="1jdr9rt5dflog">
                  <Building2Icon size={14} data-id="element-12470" data-mp="mpuzt74fvom7" />
                  {selectedBU}
                </button>
              </>}
            {selectedSM && <>
                <ChevronRightIcon size={13} className="text-blue-300/40" data-id="element-12471" data-mp="10m8bfzmakvsr" />
                <span className="flex items-center gap-1.5 px-2 py-1 rounded text-white font-semibold bg-white/5" data-id="element-12472" data-mp="vo6qta97wof6">
                  <UserIcon size={14} data-id="element-12473" data-mp="2c5kpljhiq554" />
                  {selectedSM}
                </span>
              </>}
          </div>
        </div>
      </div>

      {/* LEVEL 1: Business Units grid */}
      {!selectedBU && <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-id="element-12474" data-mp="9h2mdtz8e99v">
          {buAggregates.map(bu => <button key={bu.name} onClick={() => setSelectedBU(bu.name)} className="text-left bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm hover:bg-white/10 hover:border-blue-500/30 transition-all group" data-id="element-12475" data-mp="eg5uxoef6xml">
              <div className="flex items-start justify-between mb-4" data-id="element-12476" data-mp="o6s2qwfwajuq">
                <div className="flex items-center gap-3" data-id="element-12477" data-mp="t51wd2lz1ce8">
                  <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-blue-500/30 to-indigo-500/20 flex items-center justify-center text-blue-300" data-id="element-12478" data-mp="t3iifinsfdfm">
                    <Building2Icon size={22} data-id="element-12479" data-mp="1269wlq81snay" />
                  </div>
                  <div data-id="element-12480" data-mp="123dwbv8q9mjc">
                    <h3 className="text-base font-bold text-white leading-tight" data-id="element-12481" data-mp="2yyjsykutu6ua">
                      {bu.name}
                    </h3>
                    <p className="text-xs text-blue-300/70 mt-0.5" data-id="element-12482" data-mp="33xmzk9qggl4d">
                      {bu.salesManagers.size} sales managers · {bu.dealers.size}{' '}
                      dealers
                    </p>
                  </div>
                </div>
                {bu.needsAttention > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 text-xs font-medium" data-id="element-12483" data-mp="2mn0utewxze4j">
                    <AlertCircleIcon size={11} data-id="element-12484" data-mp="3fc7vokjvcfsy" />
                    {bu.needsAttention}
                  </span>}
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3" data-id="element-12485" data-mp="3aeerty61fxvu">
                <div data-id="element-12486" data-mp="35ea71vy3kgzt">
                  <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12487" data-mp="35dinjjhry9pw">
                    Open Pipeline
                  </p>
                  <p className="text-lg font-bold text-white mt-0.5" data-id="element-12488" data-mp="31erqtdh80u3b">
                    ${bu.openPipeline.toLocaleString()}
                  </p>
                </div>
                <div data-id="element-12489" data-mp="1pwqh7vn9kwj4">
                  <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12490" data-mp="1pzmintybm3cb">
                    Weighted
                  </p>
                  <p className="text-lg font-bold text-white mt-0.5" data-id="element-12491" data-mp="jqkv4ec5mwoi">
                    ${Math.round(bu.weightedPipeline).toLocaleString()}
                  </p>
                </div>
                <div data-id="element-12492" data-mp="tnh5kplm5svp">
                  <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12493" data-mp="tmxvtw64aasr">
                    Quotes
                  </p>
                  <p className="text-sm text-blue-100 mt-0.5" data-id="element-12494" data-mp="17h8crrbvh0o9">
                    {bu.totalQuotes} total · {bu.openCount} open
                  </p>
                </div>
                <div data-id="element-12495" data-mp="1hcs9uh26a5el">
                  <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12496" data-mp="1hb7dclwasce3">
                    Win Rate
                  </p>
                  <div className="flex items-center gap-2 mt-1" data-id="element-12497" data-mp="3j5zpjrlp6p10">
                    <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden" data-id="element-12498" data-mp="3e63itklm8jhr">
                      <div className={`h-full ${bu.winRate >= 60 ? 'bg-emerald-400' : bu.winRate >= 30 ? 'bg-amber-400' : 'bg-red-400'}`} style={{
                  width: `${bu.winRate}%`
                }} data-id="element-12499" data-mp="1to2r7k0td8nv" />
                    </div>
                    <span className={`text-xs font-semibold ${bu.winRate >= 60 ? 'text-emerald-400' : bu.winRate >= 30 ? 'text-amber-400' : 'text-red-400'}`} data-id="element-12500" data-mp="1jwyeys2iw1wq">
                      {bu.winRate}%
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-white/5 text-xs text-blue-300/80" data-id="element-12501" data-mp="1fwo719v671f8">
                <span data-id="element-12502" data-mp="110f9ccqxgw4x">{bu.countries.size} countries</span>
                <span className="flex items-center gap-1 text-blue-400 group-hover:text-white transition-colors" data-id="element-12503" data-mp="15xynlcnml8wv">
                  Drill in
                  <ChevronRightIcon size={12} data-id="element-12504" data-mp="2veg7swikfh4h" />
                </span>
              </div>
            </button>)}
        </div>}

      {/* LEVEL 2: Sales Managers in selected BU */}
      {selectedBU && !selectedSM && <div className="space-y-4" data-id="element-12505" data-mp="2ywnhk7t8t1r1">
          {(() => {
        const bu = buAggregates.find(b => b.name === selectedBU);
        if (!bu) return null;
        return <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-12506" data-mp="2owd1mfftpg86">
                <KPICard title="Sales Managers" value={bu.salesManagers.size} icon={<UsersIcon size={20} data-id="element-12508" data-mp="2kwvvrkwhih2h" />} data-id="element-12507" data-mp="2oyptbkbdv4yf" />
                <KPICard title="Open Pipeline" value={`$${bu.openPipeline.toLocaleString()}`} icon={<DollarSignIcon size={20} data-id="element-12510" data-mp="3hnog4uqgava2" />} data-id="element-12509" data-mp="3hlk9w652xk6m" />
                <KPICard title="Weighted Pipeline" value={`$${Math.round(bu.weightedPipeline).toLocaleString()}`} icon={<TrendingUpIcon size={20} data-id="element-12512" data-mp="37upd47u7j26x" />} data-id="element-12511" data-mp="3cm7gt3nm7gvl" />
                <KPICard title="Needs Attention" value={bu.needsAttention} subtitle="Across all SMs" icon={<AlertCircleIcon size={20} data-id="element-12514" data-mp="33s2t2t3s2lka" />} data-id="element-12513" data-mp="37te9c80rhuft" />
              </div>;
      })()}
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden backdrop-blur-sm" data-id="element-12515" data-mp="488j9xkzvnfz">
            <div className="px-5 py-3 border-b border-white/10 bg-black/20" data-id="element-12516" data-mp="3vjs0lwbivp9c">
              <h3 className="text-sm font-semibold text-white" data-id="element-12517" data-mp="1i3qo5j3ua6zl">
                Sales Managers in {selectedBU}
              </h3>
            </div>
            <div className="divide-y divide-white/5" data-id="element-12518" data-mp="1n3ms5icjdyf5">
              {smAggregates.map(sm => <button key={sm.name} onClick={() => setSelectedSM(sm.name)} className="w-full flex items-center gap-4 px-5 py-4 hover:bg-white/10 transition-colors text-left group" data-id="element-12519" data-mp="1r22ctfl5aupe">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center ring-2 ring-blue-400/20 flex-shrink-0" data-id="element-12520" data-mp="2111n12lk6yei">
                    <span className="text-sm font-bold text-white" data-id="element-12521" data-mp="2ar4gf3wleedd">
                      {getInitials(sm.name)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-12522" data-mp="2js2kfm716lvy">
                    <div className="flex items-center gap-2" data-id="element-12523" data-mp="2jtdl3d56jdiu">
                      <p className="text-sm font-semibold text-white truncate" data-id="element-12524" data-mp="2oqvvz92rc2th">
                        {sm.name}
                      </p>
                      {sm.needsAttention > 0 && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[10px] font-medium" data-id="element-12525" data-mp="2b2c1e9cie4zd">
                          <AlertCircleIcon size={9} data-id="element-12526" data-mp="2ktgh5ba0dtv9" />
                          {sm.needsAttention}
                        </span>}
                    </div>
                    <p className="text-xs text-blue-300/60 mt-0.5" data-id="element-12527" data-mp="1s514gvecnnn3">
                      {sm.dealers.size} dealers · {sm.totalQuotes} quotes · last
                      activity {sm.lastActivity}
                    </p>
                  </div>
                  <div className="hidden md:grid grid-cols-3 gap-6 flex-shrink-0" data-id="element-12528" data-mp="2251q4nzoyfug">
                    <div className="text-right" data-id="element-12529" data-mp="1x3ukovl0tsqy">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12530" data-mp="3divuw8t260uv">
                        Open
                      </p>
                      <p className="text-sm font-bold text-white mt-0.5" data-id="element-12531" data-mp="38jj63iimmjpn">
                        ${sm.openPipeline.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-12532" data-mp="3l37qhwnxzdti">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12533" data-mp="372naf89evw3p">
                        Weighted
                      </p>
                      <p className="text-sm font-bold text-blue-100 mt-0.5" data-id="element-12534" data-mp="3b4il33d8flu5">
                        ${Math.round(sm.weightedPipeline).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-12535" data-mp="7mqofbo5idpm">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12536" data-mp="3q213lnzbh2t5">
                        Win Rate
                      </p>
                      <p className={`text-sm font-bold mt-0.5 ${sm.winRate >= 60 ? 'text-emerald-400' : sm.winRate >= 30 ? 'text-amber-400' : 'text-red-400'}`} data-id="element-12537" data-mp="3u0qi5jw3cqed">
                        {sm.winRate}%
                      </p>
                    </div>
                  </div>
                  <ChevronRightIcon size={18} className="text-blue-300/40 group-hover:text-blue-300 group-hover:translate-x-1 transition-all flex-shrink-0" data-id="element-12538" data-mp="109gm2l7a9k7s" />
                </button>)}
            </div>
          </div>
        </div>}

      {/* LEVEL 3: Dealers managed by selected SM */}
      {selectedBU && selectedSM && <div className="space-y-4" data-id="element-12539" data-mp="mcbh6ojb2re4">
          {(() => {
        const sm = smAggregates.find(s => s.name === selectedSM);
        if (!sm) return null;
        return <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-12540" data-mp="ck5thr036w4q">
                <KPICard title="Dealers Managed" value={sm.dealers.size} icon={<BuildingIcon size={20} data-id="element-12542" data-mp="8j29qtex4fy8" />} data-id="element-12541" data-mp="8ikiycb0ytxp" />
                <KPICard title="Open Pipeline" value={`$${sm.openPipeline.toLocaleString()}`} icon={<DollarSignIcon size={20} data-id="element-12544" data-mp="3uq0uknnkb7vy" />} data-id="element-12543" data-mp="3k9941kqcjfh" />
                <KPICard title="Won (YTD)" value={`$${sm.wonAmount.toLocaleString()}`} icon={<CheckCircleIcon size={20} data-id="element-12546" data-mp="3pqnxzb6w2mg6" />} data-id="element-12545" data-mp="3uopi7zg1wrt8" />
                <KPICard title="Win Rate" value={`${sm.winRate}%`} icon={<TrendingUpIcon size={20} data-id="element-12548" data-mp="3fnshqlukzeh3" />} data-id="element-12547" data-mp="3pt0qgia9wf7h" />
              </div>;
      })()}
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden backdrop-blur-sm" data-id="element-12549" data-mp="3kltyv18g53px">
            <div className="px-5 py-3 border-b border-white/10 bg-black/20" data-id="element-12550" data-mp="3pknd4vvk4yr5">
              <h3 className="text-sm font-semibold text-white" data-id="element-12551" data-mp="3uk9oezrts4xb">
                Dealers under {selectedSM}
              </h3>
            </div>
            <div className="overflow-x-auto" data-id="element-12552" data-mp="2wxkfe5ypgs1y">
              <table className="w-full text-sm text-left" data-id="element-12553" data-mp="2wuojen6f4dg5">
                <thead className="text-xs text-blue-300 uppercase bg-black/10 border-b border-white/10" data-id="element-12554" data-mp="31wdx6al0z57x">
                  <tr data-id="element-12555" data-mp="36t4awr9cxoq6">
                    <th className="px-4 py-3 font-medium w-8" data-id="element-12556" data-mp="36sm6368mq26n"></th>
                    <th className="px-4 py-3 font-medium" data-id="element-12557" data-mp="3bm7591lyl27f">Dealer</th>
                    <th className="px-4 py-3 font-medium" data-id="element-12558" data-mp="2e6jlhwcfzmp9">Country</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-12559" data-mp="2e471zer1d4a3">Quotes</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-12560" data-mp="2j3a16ikc4ga5">Open</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-12561" data-mp="16yjnu6bnn0s">
                      Open Pipeline
                    </th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-12562" data-mp="3tc2bzbxa2zao">
                      Weighted
                    </th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-12563" data-mp="65jibp5xdyn4">Won</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-12564" data-mp="67mkt8o9lk8q">
                      Win Rate
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-12565" data-mp="g2xzjqzoargh">
                  {dealerAggregates.map(dealer => {
                const isExpanded = expandedDealer === dealer.name;
                return <Fragment key={dealer.name} data-id="element-12566" data-mp="ow1bfuuo6w90">
                        <tr onClick={() => setExpandedDealer(isExpanded ? null : dealer.name)} className={`border-b border-white/5 transition-colors cursor-pointer ${isExpanded ? 'bg-blue-500/10' : 'hover:bg-white/10'}`} data-id="element-12567" data-mp="owaypaobypb0">
                          <td className="px-4 py-3 text-blue-300" data-id="element-12568" data-mp="ys4a2qqbccr7">
                            <ChevronRightIcon size={16} className={`transition-transform ${isExpanded ? 'rotate-90 text-blue-400' : ''}`} data-id="element-12569" data-mp="1ig8umkmqswfz" />
                          </td>
                          <td className="px-4 py-3" data-id="element-12570" data-mp="18icltzha2dl1">
                            <div className="flex items-center gap-3" data-id="element-12571" data-mp="18jno1uv2rda1">
                              <div className="w-9 h-9 rounded-lg bg-blue-500/20 flex items-center justify-center text-blue-300 flex-shrink-0" data-id="element-12572" data-mp="1dfcv0j9fbwgd">
                                <BuildingIcon size={16} data-id="element-12573" data-mp="13ighu08kym5h" />
                              </div>
                              <p className="text-sm font-semibold text-white truncate" data-id="element-12574" data-mp="13kazn5ok1y5c">
                                {dealer.name}
                              </p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-blue-200" data-id="element-12575" data-mp="fsm4lhkcmvkp">
                            <div className="flex items-center gap-1.5" data-id="element-12576" data-mp="fv8e3mqsvl5k">
                              <GlobeIcon size={12} className="text-blue-400/60 flex-shrink-0" data-id="element-12577" data-mp="kr75lj7fdja7" />
                              {dealer.country}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-white font-medium" data-id="element-12578" data-mp="1gudam52qi99h">
                            {dealer.totalQuotes}
                          </td>
                          <td className="px-4 py-3 text-right text-blue-200" data-id="element-12579" data-mp="171fqjui3gc8r">
                            {dealer.openCount}
                          </td>
                          <td className="px-4 py-3 text-right text-white font-medium" data-id="element-12580" data-mp="y1sy3y7cegru">
                            ${dealer.openPipeline.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right text-blue-100" data-id="element-12581" data-mp="j6wgiudn8h23">
                            $
                            {Math.round(dealer.weightedPipeline).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right" data-id="element-12582" data-mp="2ihe162sx76c3">
                            <span className="text-emerald-400 font-medium" data-id="element-12583" data-mp="2ifjrxlr0h2nu">
                              {dealer.wonCount}
                            </span>
                            <span className="text-blue-300/40 mx-1" data-id="element-12584" data-mp="1w91scqi8obz">/</span>
                            <span className="text-red-400 font-medium" data-id="element-12585" data-mp="gju6gb312vm0">
                              {dealer.lostCount}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right" data-id="element-12586" data-mp="pks7qlm30p0w">
                            <span className={`text-xs font-semibold ${dealer.winRate >= 60 ? 'text-emerald-400' : dealer.winRate >= 30 ? 'text-amber-400' : 'text-red-400'}`} data-id="element-12587" data-mp="piz1h7fds6tf">
                              {dealer.winRate}%
                            </span>
                          </td>
                        </tr>
                        {isExpanded && <tr className="bg-black/30" data-id="element-12588" data-mp="31abe5r5y211a">
                            <td colSpan={9} className="px-4 py-4" data-id="element-12589" data-mp="31auwva3f4tgd">
                              <div className="ml-12 space-y-2" data-id="element-12590" data-mp="2wbrtrv700xb1">
                                <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold mb-2" data-id="element-12591" data-mp="2wdl019dp9fii">
                                  {dealer.quotes.length} Quote
                                  {dealer.quotes.length !== 1 ? 's' : ''}
                                </p>
                                {dealer.quotes.map(q => <div key={q.id} className="flex items-center gap-4 p-2.5 rounded-lg bg-white/5 border border-white/5 hover:bg-white/10 transition-colors" data-id="element-12592" data-mp="1e7mq7kzkurw4">
                                    <span className="text-xs font-mono text-blue-400 w-24 flex-shrink-0" data-id="element-12593" data-mp="1j4v61ex96ju7">
                                      {q.id}
                                    </span>
                                    <span className="text-sm text-white font-medium flex-1 min-w-0 truncate" data-id="element-12594" data-mp="1syvk46aiizf2">
                                      {q.customer}
                                    </span>
                                    <span className="text-xs text-blue-300/70 w-20 text-right" data-id="element-12595" data-mp="21vcpsu03y53f">
                                      {q.date}
                                    </span>
                                    <span className="text-sm text-white font-medium w-28 text-right" data-id="element-12596" data-mp="2h1s6oqluu710">
                                      ${q.amount.toLocaleString()}
                                    </span>
                                    <span className="text-xs text-blue-300 w-12 text-right" data-id="element-12597" data-mp="274ds6wjhrsbc">
                                      {q.probability}%
                                    </span>
                                    <div className="w-32 flex justify-end" data-id="element-12598" data-mp="2zt2towoglznm">
                                      <StatusBadge status={q.status} data-id="element-12599" data-mp="2ut6s1438ikb8" />
                                    </div>
                                  </div>)}
                              </div>
                            </td>
                          </tr>}
                      </Fragment>;
              })}
                </tbody>
              </table>
            </div>
          </div>
        </div>}
    </div>;
}
// --- Dealers View ---
interface DealerAggregate {
  name: string;
  country: string;
  totalQuotes: number;
  openCount: number;
  wonCount: number;
  lostCount: number;
  openPipeline: number;
  weightedPipeline: number;
  wonAmount: number;
  winRate: number;
  needsAttention: number;
  lastActivity: string;
  quotes: Quote[];
}
function aggregateByDealer(quotes: Quote[]): DealerAggregate[] {
  const closedStatuses: QuoteStatus[] = ['Won', 'Lost', 'Expired', 'Cancelled'];
  const map = new Map<string, DealerAggregate>();
  quotes.forEach(q => {
    const existing = map.get(q.dealer) || {
      name: q.dealer,
      country: q.country,
      totalQuotes: 0,
      openCount: 0,
      wonCount: 0,
      lostCount: 0,
      openPipeline: 0,
      weightedPipeline: 0,
      wonAmount: 0,
      winRate: 0,
      needsAttention: 0,
      lastActivity: q.lastUpdated,
      quotes: []
    };
    existing.totalQuotes += 1;
    existing.quotes.push(q);
    if (!closedStatuses.includes(q.status)) {
      existing.openCount += 1;
      existing.openPipeline += q.amount;
      existing.weightedPipeline += q.amount * q.probability / 100;
    }
    if (q.status === 'Won') {
      existing.wonCount += 1;
      existing.wonAmount += q.amount;
    }
    if (q.status === 'Lost' || q.status === 'Expired') {
      existing.lostCount += 1;
    }
    if (q.needsAttention) existing.needsAttention += 1;
    if (q.lastUpdated > existing.lastActivity) existing.lastActivity = q.lastUpdated;
    map.set(q.dealer, existing);
  });
  // Compute win rate
  const result = Array.from(map.values()).map(d => {
    const decided = d.wonCount + d.lostCount;
    return {
      ...d,
      winRate: decided > 0 ? Math.round(d.wonCount / decided * 100) : 0
    };
  });
  // Sort by open pipeline desc
  return result.sort((a, b) => b.openPipeline - a.openPipeline);
}
type DealerGroupBy = 'none' | 'probability' | 'status';
function DealerQuotesPanel({
  quotes,
  groupBy,
  onGroupByChange
}: {
  quotes: Quote[];
  groupBy: DealerGroupBy;
  onGroupByChange: (g: DealerGroupBy) => void;
}) {
  // Build groups based on selected criterion
  const groups = useMemo(() => {
    if (groupBy === 'none') {
      return [{
        key: 'all',
        label: `${quotes.length} Quote${quotes.length !== 1 ? 's' : ''}`,
        accent: 'text-blue-400/60',
        dotColor: 'bg-blue-400/40',
        quotes: quotes
      }];
    }
    if (groupBy === 'probability') {
      const high = quotes.filter(q => q.probability > 70);
      const mid = quotes.filter(q => q.probability >= 30 && q.probability <= 70);
      const low = quotes.filter(q => q.probability < 30);
      return [{
        key: 'high',
        label: 'High (>70%)',
        accent: 'text-emerald-400',
        dotColor: 'bg-emerald-400',
        quotes: high
      }, {
        key: 'mid',
        label: 'Medium (30–70%)',
        accent: 'text-amber-400',
        dotColor: 'bg-amber-400',
        quotes: mid
      }, {
        key: 'low',
        label: 'Low (<30%)',
        accent: 'text-red-400',
        dotColor: 'bg-red-400',
        quotes: low
      }].filter(g => g.quotes.length > 0);
    }
    // groupBy === 'status'
    const order: QuoteStatus[] = ['Draft', 'Active', 'Expired', 'Won', 'Lost', 'Cancelled'];
    return order.map(status => ({
      key: status,
      label: status,
      accent: 'text-blue-300',
      dotColor: 'bg-blue-400/40',
      quotes: quotes.filter(q => q.status === status)
    })).filter(g => g.quotes.length > 0);
  }, [quotes, groupBy]);
  const groupOptions: {
    value: DealerGroupBy;
    label: string;
  }[] = [{
    value: 'none',
    label: 'None'
  }, {
    value: 'probability',
    label: 'Probability'
  }, {
    value: 'status',
    label: 'Status'
  }];
  return <div className="space-y-3" data-id="element-12600" data-mp="3hv1oh7wp48mj">
      {/* Header with group-by selector */}
      <div className="flex items-center justify-between flex-wrap gap-2" data-id="element-12601" data-mp="3cvfb8ygvasdq">
        <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12602" data-mp="3cxjede6xzncy">
          {quotes.length} Quote{quotes.length !== 1 ? 's' : ''}
        </p>
        <div className="flex items-center gap-2" data-id="element-12603" data-mp="10mglk6e7tokn">
          <span className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12604" data-mp="14mqpldig64wv">
            Group by
          </span>
          <div className="flex bg-white/5 border border-white/10 rounded-lg p-0.5" data-id="element-12605" data-mp="qnr4ugy22a5z">
            {groupOptions.map(opt => <button key={opt.value} onClick={() => onGroupByChange(opt.value)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${groupBy === opt.value ? 'bg-blue-600 text-white' : 'text-blue-300 hover:text-white hover:bg-white/5'}`} data-id="element-12606" data-mp="vkzic489dq0w">
                {opt.label}
              </button>)}
          </div>
        </div>
      </div>

      {/* Groups */}
      <div className="space-y-4" data-id="element-12607" data-mp="cv1dry3o6f20">
        {groups.map(group => {
        const groupTotal = group.quotes.reduce((acc, q) => acc + q.amount, 0);
        const groupWeighted = group.quotes.reduce((acc, q) => acc + q.amount * q.probability / 100, 0);
        return <div key={group.key} data-id="element-12608" data-mp="2zhkzkkejwhh">
              {groupBy !== 'none' && <div className="flex items-center justify-between mb-2 px-1" data-id="element-12609" data-mp="3fg7n1m3nznbv">
                  <div className="flex items-center gap-2" data-id="element-12610" data-mp="3bfnpwij3cjpq">
                    <span className={`w-1.5 h-1.5 rounded-full ${group.dotColor}`} data-id="element-12611" data-mp="3bhgw5wpsl1x7" />
                    <span className={`text-xs font-semibold ${group.accent}`} data-id="element-12612" data-mp="3pgqasrycnnz2">
                      {group.label}
                    </span>
                    <span className="text-xs text-blue-300/50" data-id="element-12613" data-mp="2a7h556zynbi">
                      · {group.quotes.length} quote
                      {group.quotes.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs" data-id="element-12614" data-mp="c0io70lz9cmm">
                    <span className="text-blue-300/60" data-id="element-12615" data-mp="7162ij6ueblm">
                      Total:{' '}
                      <span className="text-white font-medium" data-id="element-12616" data-mp="lyg85bwt0n9f">
                        ${groupTotal.toLocaleString()}
                      </span>
                    </span>
                    <span className="text-blue-300/60" data-id="element-12617" data-mp="h16ko1sz6lo8">
                      Weighted:{' '}
                      <span className="text-white font-medium" data-id="element-12618" data-mp="1id947hhteth6">
                        ${Math.round(groupWeighted).toLocaleString()}
                      </span>
                    </span>
                  </div>
                </div>}
              <div className="space-y-2" data-id="element-12619" data-mp="14e9gqd61gkml">
                {group.quotes.map(q => <div key={q.id} className="flex items-center gap-4 p-2.5 rounded-lg bg-white/5 border border-white/5 hover:bg-white/10 transition-colors" data-id="element-12620" data-mp="14dzo0htlzzd7">
                    <span className="text-xs font-mono text-blue-400 w-24 flex-shrink-0" data-id="element-12621" data-mp="uny2nxsn4v1z">
                      {q.id}
                    </span>
                    <span className="text-sm text-white font-medium flex-1 min-w-0 truncate" data-id="element-12622" data-mp="ppmojavbbydy">
                      {q.customer}
                    </span>
                    <span className="text-xs text-blue-300/70 w-20 text-right" data-id="element-12623" data-mp="goya4f20bw0j">
                      {q.date}
                    </span>
                    <span className="text-sm text-white font-medium w-28 text-right" data-id="element-12624" data-mp="bqdktaoamwqv">
                      ${q.amount.toLocaleString()}
                    </span>
                    <span className="text-xs text-blue-300 w-12 text-right" data-id="element-12625" data-mp="2sb8ule9kt9lt">
                      {q.probability}%
                    </span>
                    <div className="w-32 flex justify-end" data-id="element-12626" data-mp="2x90m476b6tph">
                      <StatusBadge status={q.status} data-id="element-12627" data-mp="31bouyev0w0mg" />
                    </div>
                  </div>)}
              </div>
            </div>;
      })}
      </div>
    </div>;
}
function DealersView() {
  const dummyQuotes = useQuotes();
  const [search, setSearch] = useState('');
  const [expandedDealer, setExpandedDealer] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<DealerGroupBy>('none');
  const [sortBy, setSortBy] = useState<'openPipeline' | 'weighted' | 'totalQuotes' | 'winRate'>('openPipeline');
  const dealers = useMemo(() => aggregateByDealer(dummyQuotes), [dummyQuotes]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q ? dealers.filter(d => d.name.toLowerCase().includes(q) || d.country.toLowerCase().includes(q)) : dealers;
    const sorted = [...base].sort((a, b) => {
      if (sortBy === 'openPipeline') return b.openPipeline - a.openPipeline;
      if (sortBy === 'weighted') return b.weightedPipeline - a.weightedPipeline;
      if (sortBy === 'totalQuotes') return b.totalQuotes - a.totalQuotes;
      return b.winRate - a.winRate;
    });
    return sorted;
  }, [dealers, search, sortBy]);
  // Totals across all dealers (unfiltered, for KPI cards)
  const totals = useMemo(() => {
    return dealers.reduce((acc, d) => ({
      dealers: acc.dealers + 1,
      active: acc.active + (d.openCount > 0 ? 1 : 0),
      openPipeline: acc.openPipeline + d.openPipeline,
      weighted: acc.weighted + d.weightedPipeline,
      attention: acc.attention + d.needsAttention
    }), {
      dealers: 0,
      active: 0,
      openPipeline: 0,
      weighted: 0,
      attention: 0
    });
  }, [dealers]);
  return <div className="space-y-6" data-id="element-12628" data-mp="20y9kl5p2p6q">
      <div className="flex items-center justify-between flex-wrap gap-3" data-id="element-12629" data-mp="20ojkxkngi11">
        <div data-id="element-12630" data-mp="71ucaveb85ak">
          <h2 className="text-xl font-bold text-white" data-id="element-12631" data-mp="72nm658kfawx">Quotes by Dealer</h2>
          <p className="text-sm text-blue-300/70 mt-0.5" data-id="element-12632" data-mp="3oa9b6p8d1lfx">
            Pipeline performance grouped by dealer
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-12633" data-mp="3tadfzhzworqb">
          <div className="relative" data-id="element-12634" data-mp="ks7j2ufgtdyb">
            <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-300/50" data-id="element-12635" data-mp="kt97s1pelyyi" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search dealer..." className="bg-white/5 border border-white/10 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 w-64" data-id="element-12636" data-mp="pqsod89kqntm" />
          </div>
          <div className="relative" data-id="element-12637" data-mp="13p8ryqc53y7j">
            <select value={sortBy} onChange={e => setSortBy(e.target.value as typeof sortBy)} className="appearance-none bg-white/5 border border-white/10 rounded-lg pl-3 pr-9 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer" data-id="element-12638" data-mp="18ilgz1kqe70t">
              <option value="openPipeline" className="bg-slate-800" data-id="element-12639" data-mp="3c2e5po4t8ra6">
                Sort: Open Pipeline
              </option>
              <option value="weighted" className="bg-slate-800" data-id="element-12640" data-mp="322dhbns33kz4">
                Sort: Weighted
              </option>
              <option value="totalQuotes" className="bg-slate-800" data-id="element-12641" data-mp="3j6n825q98m7">
                Sort: Quote Count
              </option>
              <option value="winRate" className="bg-slate-800" data-id="element-12642" data-mp="3urb15vby1ptz">
                Sort: Win Rate
              </option>
            </select>
            <ChevronRightIcon size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-blue-300 rotate-90 pointer-events-none" data-id="element-12643" data-mp="3l0zl5aezjq6q" />
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-12644" data-mp="hk984bkzkcvj">
        <KPICard title="Active Dealers" value={`${totals.active} / ${totals.dealers}`} subtitle="With open quotes" icon={<BuildingIcon size={20} data-id="element-12646" data-mp="puzxsqhtp4q2" />} data-id="element-12645" data-mp="hhwirdcwf08g" />
        <KPICard title="Open Pipeline" value={`${totals.openPipeline.toLocaleString()}`} icon={<DollarSignIcon size={20} data-id="element-12648" data-mp="yssdhvrgiedz" />} data-id="element-12647" data-mp="ttgueg3mawt8" />
        <KPICard title="Weighted Pipeline" value={`${Math.round(totals.weighted).toLocaleString()}`} icon={<TrendingUpIcon size={20} data-id="element-12650" data-mp="13tzkvtpotbk4" />} data-id="element-12649" data-mp="yrqpkqpcdxeu" />
        <KPICard title="Needs Attention" value={totals.attention} subtitle="Across all dealers" icon={<AlertCircleIcon size={20} data-id="element-12652" data-mp="18pom01hcv0ij" />} data-id="element-12651" data-mp="18rhl8vpn2igi" />
      </div>

      {/* Dealer table */}
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden backdrop-blur-sm" data-id="element-12653" data-mp="1cr9z9nxsxgt9">
        <div className="overflow-x-auto" data-id="element-12654" data-mp="1hlwa9pzz17ct">
          <table className="w-full text-sm text-left" data-id="element-12655" data-mp="1mjfl0fxgn200">
            <thead className="text-xs text-blue-300 uppercase bg-black/20 border-b border-white/10" data-id="element-12656" data-mp="1ml0h49zfaszz">
              <tr data-id="element-12657" data-mp="1rfwc9izyhwhb">
                <th className="px-4 py-3 font-medium w-8" data-id="element-12658" data-mp="1wh3ktk9xazp1"></th>
                <th className="px-4 py-3 font-medium" data-id="element-12659" data-mp="1whbw57qedwy4">Dealer</th>
                <th className="px-4 py-3 font-medium" data-id="element-12660" data-mp="16s574a6w624q">Country</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-12661" data-mp="11tv9zu1lwrgh">Quotes</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-12662" data-mp="11rqusfy9c60w">Open</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-12663" data-mp="1gjj9qqu53i23">
                  Open Pipeline
                </th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-12664" data-mp="1bqq7c9m58lt6">Weighted</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-12665" data-mp="1pi4o09skwax2">Won</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-12666" data-mp="1kkuuoj22jf3y">Win Rate</th>
                <th className="px-4 py-3 font-medium text-center" data-id="element-12667" data-mp="1zhlqyjgg4apc">Alerts</th>
              </tr>
            </thead>
            <tbody data-id="element-12668" data-mp="1uhzh8jzttcla">
              {filtered.length === 0 && <tr data-id="element-12669" data-mp="1ujajughjceat">
                  <td colSpan={10} className="px-4 py-10 text-center text-sm text-blue-300/60" data-id="element-12670" data-mp="1ugodgk6ds4u6">
                    No dealers match your search.
                  </td>
                </tr>}
              {filtered.map(dealer => {
              const isExpanded = expandedDealer === dealer.name;
              return <Fragment key={dealer.name} data-id="element-12671" data-mp="2bu7gdyfkf7vj">
                    <tr onClick={() => setExpandedDealer(isExpanded ? null : dealer.name)} className={`border-b border-white/5 transition-colors cursor-pointer ${isExpanded ? 'bg-blue-500/10' : 'hover:bg-white/10'}`} data-id="element-12672" data-mp="2gpwgq3zesssw">
                      <td className="px-4 py-3 text-blue-300" data-id="element-12673" data-mp="26zjywjizbdqg">
                        <ChevronRightIcon size={16} className={`transition-transform ${isExpanded ? 'rotate-90 text-blue-400' : ''}`} data-id="element-12674" data-mp="1t0tzuycln3zg" />
                      </td>
                      <td className="px-4 py-3" data-id="element-12675" data-mp="1x0m2yvj84hxf">
                        <div className="flex items-center gap-3" data-id="element-12676" data-mp="1j47k2nz4r34a">
                          <div className="w-9 h-9 rounded-lg bg-blue-500/20 flex items-center justify-center text-blue-300 flex-shrink-0" data-id="element-12677" data-mp="1o20nxacynl1e">
                            <BuildingIcon size={16} data-id="element-12678" data-mp="3dii84u7wht90" />
                          </div>
                          <div className="min-w-0" data-id="element-12679" data-mp="3igjrzhd5hwlj">
                            <p className="text-sm font-semibold text-white truncate" data-id="element-12680" data-mp="3idxkfhq9fh3b">
                              {dealer.name}
                            </p>
                            <p className="text-xs text-blue-300/60 truncate" data-id="element-12681" data-mp="2194wh515lkmr">
                              Last activity: {dealer.lastActivity}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-blue-200" data-id="element-12682" data-mp="1mip8jihcrykw">
                        <div className="flex items-center gap-1.5" data-id="element-12683" data-mp="1mguo059zu8hc">
                          <GlobeIcon size={12} className="text-blue-400/60 flex-shrink-0" data-id="element-12684" data-mp="1hj2ytj0qh0gu" />
                          {dealer.country}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-white font-medium" data-id="element-12685" data-mp="18k7qo1e4pgaw">
                        {dealer.totalQuotes}
                      </td>
                      <td className="px-4 py-3 text-right text-blue-200" data-id="element-12686" data-mp="32x7e2gor3esq">
                        {dealer.openCount}
                      </td>
                      <td className="px-4 py-3 text-right text-white font-medium" data-id="element-12687" data-mp="3afot12feblt3">
                        ${dealer.openPipeline.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right text-blue-100" data-id="element-12688" data-mp="3k5zv0jfirkxc">
                        ${Math.round(dealer.weightedPipeline).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-12689" data-mp="2rhua9wogtvxi">
                        <span className="text-emerald-400 font-medium" data-id="element-12690" data-mp="2rjf5zpminkwy">
                          {dealer.wonCount}
                        </span>
                        <span className="text-blue-300/40 mx-1" data-id="element-12691" data-mp="31ga2k2z8bb8l">/</span>
                        <span className="text-red-400 font-medium" data-id="element-12692" data-mp="31i4q7p1vxhgd">
                          {dealer.lostCount}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-12693" data-mp="gp17u0gs7gen">
                        <div className="inline-flex items-center gap-2 min-w-[80px] justify-end" data-id="element-12694" data-mp="kr4nl1ahh75q">
                          <div className="w-12 h-1.5 rounded-full bg-white/10 overflow-hidden flex-shrink-0" data-id="element-12695" data-mp="lpd9a8s3ifhp">
                            <div className={`h-full ${dealer.winRate >= 60 ? 'bg-emerald-400' : dealer.winRate >= 30 ? 'bg-amber-400' : 'bg-red-400'}`} style={{
                          width: `${dealer.winRate}%`
                        }} data-id="element-12696" data-mp="ppx2j19jsyyk" />
                          </div>
                          <span className={`text-xs font-semibold w-9 text-right ${dealer.winRate >= 60 ? 'text-emerald-400' : dealer.winRate >= 30 ? 'text-amber-400' : 'text-red-400'}`} data-id="element-12697" data-mp="2q91f3cairrm">
                            {dealer.winRate}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center" data-id="element-12698" data-mp="gqjtuat90eew">
                        {dealer.needsAttention > 0 ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 text-xs font-medium" data-id="element-12699" data-mp="1n6m1mn745n49">
                            <AlertCircleIcon size={11} data-id="element-12700" data-mp="3cn855kgj562f" />
                            {dealer.needsAttention}
                          </span> : <span className="text-blue-300/30" data-id="element-12701" data-mp="37nlg8drca1ds">—</span>}
                      </td>
                    </tr>
                    {isExpanded && <tr className="bg-black/30" data-id="element-12702" data-mp="32w3psjmxzj72">
                        <td colSpan={10} className="px-4 py-4" data-id="element-12703" data-mp="2xybnqwsl2gp5">
                          <div className="ml-12" data-id="element-12704" data-mp="2xy1wz6zps5ie">
                            <DealerQuotesPanel quotes={dealer.quotes} groupBy={groupBy} onGroupByChange={setGroupBy} data-id="element-12705" data-mp="2tx9nqdque0m4" />
                          </div>
                        </td>
                      </tr>}
                  </Fragment>;
            })}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}
function PlaceholderView({
  title,
  description
}: {
  title: string;
  description: string;
}) {
  return <div className="flex-1 flex items-center justify-center min-h-[60vh]" data-id="element-12706" data-mp="i64mingudyas">
      <div className="text-center max-w-md" data-id="element-12707" data-mp="d7l06lyc6khw">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/5 border border-white/10 text-blue-400 shadow-lg mb-4" data-id="element-12708" data-mp="d8w1mf4b7g5u">
          <ClockIcon size={24} data-id="element-12709" data-mp="2cfjknfs0j97b" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2" data-id="element-12710" data-mp="2cgchnp166kc9">{title}</h2>
        <p className="text-sm text-blue-300/70 leading-relaxed" data-id="element-12711" data-mp="1xr6l68tpv882">
          {description}
        </p>
      </div>
    </div>;
}
// --- Main Component ---
export function QuotesPageV2() {
  const {
    role
  } = useUser();
  const [activeNav, setActiveNav] = useState<NavItem>('quotes');
  const [configOpen, setConfigOpen] = useState(true);
  const explorerActive = activeNav === 'quotes';
  const navItems = [{
    id: 'quotes',
    label: 'Quote Explorer',
    icon: <ListIcon size={16} data-id="element-12712" data-mp="2lfpqqgldt6wg" />
  }, {
    id: 'overview',
    label: 'Overview',
    icon: <LayoutDashboardIcon size={16} data-id="element-12713" data-mp="2lhsppq4iiadb" />
  }, {
    id: 'pipeline',
    label: 'Pipeline',
    icon: <KanbanSquareIcon size={16} data-id="element-12714" data-mp="161234gtfixa0" />
  }, {
    id: 'attention',
    label: 'Needs Attention',
    icon: <AlertCircleIcon size={16} data-id="element-12715" data-mp="19yga01tn0syu" />
  }];
  return <div className="flex flex-col h-screen bg-[#1B2A52] overflow-hidden" data-id="element-12716" data-mp="12gie504dpge3">
      {/* Header with breadcrumb + top-level tabs — homogeneous with the main sidebar's top (blue-950) */}
      <div className="bg-[#172554] border-b border-white/10 px-6 relative z-10 flex-shrink-0 h-[72px] flex items-center" data-id="element-12717" data-mp="1cf7qkddia8my">
        {/* Breadcrumb (left) */}
        <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-12718" data-mp="17gnzxznyw8o9">
          <HomeIcon size={15} className="text-blue-300/60 hover:text-blue-200 cursor-pointer transition-colors flex-shrink-0" data-id="element-12719" data-mp="jpr0vnh9t5uf" />
          <ChevronRightIcon size={13} className="text-blue-300/40 flex-shrink-0" data-id="element-12720" data-mp="et0ll2daiea2" />
          <span className="text-xs text-blue-300/60" data-id="element-12721" data-mp="thethrpkcsxh">Sales Pipeline</span>
        </div>
        {/* Top tabs — centered */}
        <nav className="absolute left-1/2 -translate-x-1/2 flex items-stretch gap-1 h-full" aria-label="Quotes navigation" data-id="element-12722" data-mp="oobzjigvjhgr">
          {navItems.map(item => {
          const isActive = activeNav === item.id;
          return <button key={item.id} onClick={() => setActiveNav(item.id as NavItem)} className={`relative flex items-center gap-2 px-4 text-sm font-medium transition-colors border-b-2 ${isActive ? 'text-white border-blue-400' : 'text-blue-300/70 hover:text-white border-transparent hover:border-white/20'}`} data-id="element-12723" data-mp="3s499prf1cpf3">
                <span className={isActive ? 'text-blue-300' : 'text-blue-400/60'} data-id="element-12724" data-mp="396dc7ckxpb7t">
                  {item.icon}
                </span>
                {item.label}
                {item.id === 'attention' && <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-id="element-12725" data-mp="3e7swh2fafdp5">
                    1
                  </span>}
              </button>;
        })}
        </nav>
      </div>

      {/* Main Layout — soft navy canvas, one step lighter than the top bar */}
      <div className="flex flex-1 overflow-hidden relative z-10 bg-[#1E2E58]" data-id="element-12726" data-mp="3s351c2n5qkjs">
        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 no-scrollbar transition-[padding] duration-200" style={{
        paddingRight: explorerActive && configOpen ? 288 : undefined
      }} data-id="element-12727" data-mp="1suhtfl900h2s">
          <div className="max-w-7xl mx-auto h-full" data-id="element-12728" data-mp="1q9s7jwuqi287">
            {activeNav === 'overview' && <OverviewView role={role} data-id="element-12729" data-mp="1lfnyn81wrk4h" />}
            {activeNav === 'pipeline' && <PipelineView data-id="element-12730" data-mp="1gixjcmxxgsk4" />}
            {activeNav === 'quotes' && <AllQuotesExplorerView role={role} configOpen={configOpen} setConfigOpen={setConfigOpen} data-id="element-12731" data-mp="1gfjjblcqkvfu" />}
            {activeNav === 'attention' && <PlaceholderView title="Needs Attention" description="Quotes that require your immediate action, such as expiring validity, missing information, or stalled negotiations." data-id="element-12732" data-mp="296tdbjw446d7" />}
          </div>
        </div>
      </div>
    </div>;
}